import { Directive, Injectable, OnDestroy } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ParametresService } from '../core/parametres.service';
import { alertesLibelle } from './questionnaire.metadata';

@Directive()
@Injectable({
  providedIn: 'root'
})
export class AlertesService implements OnDestroy {
  // variable contenant les libellés
  public alertesLibelle: Map<string, string>;

  /*
   * Différentes variables utiles pour gérer les alertes.
   * Une pour chaque onglet, plus deux générales
   */
  public mapAlertesDG: Map<string, boolean> = new Map<string, boolean>();
  public mapAlertesMoyens: Map<string, boolean> = new Map<string, boolean>();
  public mapAlertesPremiersContacts: Map<string, boolean> = new Map<string, boolean>();
  public mapAlertesPersonnesRecues: Map<string, boolean> = new Map<string, boolean>();
  public mapAlertesContexteFaitsProcedure: Map<string, boolean> = new Map<string, boolean>();
  public mapAlertesAidesApportees: Map<string, boolean> = new Map<string, boolean>();
  public mapAlertesBav: Map<string, boolean> = new Map<string, boolean>();
  public mapAlertes: Map<string, Map<string, boolean>> = new Map<string, Map<string, boolean>>()
    .set('dg', this.mapAlertesDG)
    .set('moyens', this.mapAlertesMoyens)
    .set('premiersContacts', this.mapAlertesPremiersContacts)
    .set('personnesRecues', this.mapAlertesPersonnesRecues)
    .set('contexteFaitsProcedure', this.mapAlertesContexteFaitsProcedure)
    .set('aidesApportees', this.mapAlertesAidesApportees)
    .set('bav', this.mapAlertesBav)
    ;

  private mapAlertes$: Map<string, BehaviorSubject<boolean>> = new Map<string, BehaviorSubject<boolean>>();

  /*
   * Variables pour gérer l’affichage de la coloration de l’onglet en cas d’alertes
   */
  private aAlerteDG$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private aAlerteMoyens$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private aAlertePremiersContacts$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private aAlertePersonnesRecues$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private aAlerteContexteFaitsProcedure$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private aAlerteAidesApportees$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private aAlerteBav$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public aAlerte$: Map<string, BehaviorSubject<boolean>> = new Map<string, BehaviorSubject<boolean>>()
    .set('dg', this.aAlerteDG$)
    .set('moyens', this.aAlerteMoyens$)
    .set('premiersContacts', this.aAlertePremiersContacts$)
    .set('personnesRecues', this.aAlertePersonnesRecues$)
    .set('contexteFaitsProcedure', this.aAlerteContexteFaitsProcedure$)
    .set('aidesApportees', this.aAlerteAidesApportees$)
    .set('bav', this.aAlerteBav$)
    ;

  /**
   * Gère la latence entre chaque test sur les alertes
   * Possible de le réduire, mais augmente le nombre de calcul
   */
  private debounceTime: number = 200;
  public anneeEnquete: string;
  protected subTab: Subscription[] = [];

  constructor(
    private anneeService: ParametresService
  ) {
    // Recuperation de l'annee collecte
    this.subTab.push(this.anneeService.getAnnee$().subscribe(annee => {
      this.anneeEnquete = annee;
    }));
    // Init libelles alerte
    this.initLibelleErreursCroisees();
  }

  ngOnDestroy(): void {
    if (this.subTab.length !== 0) {
      this.subTab.forEach(element => element.unsubscribe());
    }
  }

  /**
   * Procédure pour mettre à jour l’état d’une alerte
   * Met également à jour les indicateurs sur la présence d’une alerte dans un onglet
   */
  private miseAJourAlerte(onglet: string, label: string, valeur: boolean): void {
    this.mapAlertes.get(onglet).set(label, valeur);
    // Pour le BehaviorSubject, ne pas recréer l’objet à chaque fois
    if (this.mapAlertes$.has(label)) {
      this.mapAlertes$.get(label).next(valeur);
    } else {
      // Il faut créer notre BehaviorSubject
      this.mapAlertes$.set(label, new BehaviorSubject(valeur));
    }
    /**
     * Gestion des draps sur l’état d’une alerte
     */
    if (valeur) {
      this.aAlerte$.get(onglet).next(true);
    } else if (this.aAlerte$.get(onglet).value) {
      // pas besoin d’enchainer les ajout de false, cette condition est faite pour ça
      // évite de faire trop de traitements
      this.aAlerte$.get(onglet).next(this.mapAAlerteActive(this.mapAlertes.get(onglet)));
    }
  }
  /**
   * Fonction permettant d’initialiser la map des libellés des alertes.
   * Actuellement en dur dans questionnaire.metadata.ts, mais possible de contacter un WS.
   */
  private initLibelleErreursCroisees(): void {
    this.alertesLibelle = alertesLibelle;
  }

  /** Procédure à appeler lors de la création du formulaire pour lancer la vérification des alertes */
  public ajoutAlertes(rootForm: UntypedFormGroup): void {
    rootForm.valueChanges.pipe(distinctUntilChanged(), debounceTime(this.debounceTime)).subscribe((formulaire) => {
      this.testDg(formulaire);
    });

    // Spécificité pour les boucles des lieux de permanence sur l'onglet Moyens
    rootForm.valueChanges.pipe(distinctUntilChanged(), debounceTime(this.debounceTime)).subscribe((formulaire) => {
      this.testMoyens(formulaire);
    });

    rootForm.valueChanges.pipe(distinctUntilChanged(), debounceTime(this.debounceTime)).subscribe((formulaire) => {
      this.testPremiersContacts(formulaire);
    });

    // Spécificité pour la boucle des Tj sur l'onglet Personnes recues
    rootForm.valueChanges.pipe(distinctUntilChanged(), debounceTime(this.debounceTime)).subscribe((formulaire) => {
      this.testPersonnesRecues(formulaire);
    });

    rootForm.valueChanges.pipe(distinctUntilChanged(), debounceTime(this.debounceTime)).subscribe((formulaire) => {
      this.testContexteFaitsProcedure(formulaire);
    });

    rootForm.valueChanges.pipe(distinctUntilChanged(), debounceTime(this.debounceTime)).subscribe((formulaire) => {
      this.testAidesApportees(formulaire);
    });

    rootForm.valueChanges.pipe(distinctUntilChanged(), debounceTime(this.debounceTime)).subscribe((formulaire) => {
      this.testBav(formulaire);
    });

    rootForm.updateValueAndValidity();
  }

  /** Fonction permettant de suivre l’état d’une alerte via une observable */
  public alerte$(label: string): Observable<boolean> {
    if (!this.mapAlertes$.has(label)) {
      this.mapAlertes$.set(label, new BehaviorSubject(false));
    }
    return this.mapAlertes$.get(label).asObservable();
  }

  /**
   * Fonction parcourant une Map pour déterminer si une alerte est active
   * @param map Map à analyser
   */
  private mapAAlerteActive(map: Map<string, boolean>): boolean {
    let result = false;
    map.forEach((value) => {
      result = result || value;
    });
    return result;
  }

  /** Procédures des alertes à tester */
  // Onglet DG 
  private testDg(formulaire: any): void {
    this.miseAJourAlerte('dg', 'alertDg1', this.alertDg1(formulaire));
    this.miseAJourAlerte('dg', 'alertDg2', this.alertDg2(formulaire));
    this.miseAJourAlerte('dg', 'alertDg3', this.alertDg3(formulaire));
    this.miseAJourAlerte('dg', 'alertDg4', this.alertDg4(formulaire));
    this.miseAJourAlerte('dg', 'alertDg5', this.alertDg5(formulaire));
    this.miseAJourAlerte('dg', 'alertDg6', this.alertDg6(formulaire));
    this.miseAJourAlerte('dg', 'alertDg7', this.alertDg7(formulaire));
  }

  private alertDg1(formulaire: any): boolean {
    const xxTjNb = Number(formulaire['dg']['xxTjNb']);

    return xxTjNb === 0;
  }

  private alertDg2(formulaire: any): boolean {
    const xxTjNb = Number(formulaire['dg']['xxTjNb']);

    return xxTjNb > 5;
  }

  private alertDg3(formulaire: any): boolean {
    const xxBavNb = Number(formulaire['dg']['xxBavNb']);

    return xxBavNb === 0;
  }

  private alertDg4(formulaire: any): boolean {
    const xxBavNb = Number(formulaire['dg']['xxBavNb']);

    return xxBavNb > 5;
  }

  private alertDg5(formulaire: any): boolean {
    const xxRecuTot = formulaire['dg']['xxRecuTot'];
    const xxRecuVict = Number(formulaire['dg']['xxRecuVict']);
    const xxRecuNvict = Number(formulaire['dg']['xxRecuNvict']);

    const sumTableau = xxRecuVict + xxRecuNvict;

    return xxRecuTot !== null && Number(xxRecuTot) > sumTableau;
  }

  private alertDg6(formulaire: any): boolean {
    const xxPremContactRecuTot = formulaire['dg']['xxPremContactRecuTot'];
    const xxPremContactRecuVict = Number(formulaire['dg']['xxPremContactRecuVict']);
    const xxPremContactRecuNvict = Number(formulaire['dg']['xxPremContactRecuNvict']);

    const sumTableau = xxPremContactRecuVict + xxPremContactRecuNvict;


    return xxPremContactRecuTot !== null && Number(xxPremContactRecuTot) > sumTableau;
  }

  private alertDg7(formulaire: any): boolean {
    const xxPremContactNrecuTot = formulaire['dg']['xxPremContactNrecuTot'];
    const xxPremContactNrecuVict = Number(formulaire['dg']['xxPremContactNrecuVict']);
    const xxPremContactNrecuNvict = Number(formulaire['dg']['xxPremContactNrecuNvict']);

    const sumTableau = xxPremContactNrecuVict + xxPremContactNrecuNvict;

    return xxPremContactNrecuTot !== null && Number(xxPremContactNrecuTot) > sumTableau;
  }

  // RQ : dans les fonctions qui font des comparaison de montants decimaux, penser à formater la variable aux nombres de decimales souhaitees!
  // avec parseFloat(nomVar).toFixed(nbdecimale)

  /* Onglet Moyens : attention certaines variables = boucle */
  private testMoyens(formulaire: any): void {
    if (formulaire) {
      // Alertes générales de l'onglet moyens
      this.miseAJourAlerte('moyens', 'alertA9', this.alertA9(formulaire));
      this.miseAJourAlerte('moyens', 'alertA10', this.alertA10(formulaire));
      this.miseAJourAlerte('moyens', 'alertA11', this.alertA11(formulaire));
      this.miseAJourAlerte('moyens', 'alertA12', this.alertA12(formulaire));
      this.miseAJourAlerte('moyens', 'alertA13', this.alertA13(formulaire));
      this.miseAJourAlerte('moyens', 'alertA14', this.alertA14(formulaire));
      this.miseAJourAlerte('moyens', 'alertA15', this.alertA15(formulaire));
      this.miseAJourAlerte('moyens', 'alertA16', this.alertA16(formulaire));
      this.miseAJourAlerte('moyens', 'alertA37', this.alertA37(formulaire));
      this.miseAJourAlerte('moyens', 'alertA38', this.alertA38(formulaire));
      this.miseAJourAlerte('moyens', 'alertA57', this.alertA57(formulaire));
      this.miseAJourAlerte('moyens', 'alertA58', this.alertA58(formulaire));
      this.miseAJourAlerte('moyens', 'alertA59', this.alertA59(formulaire));

      // Alertes sur Boucles pour les lieux de permanence de moyens
      // 1 - Assos
      const assos: any[] = formulaire['moyens']['lieuxAsso'];
      // nettoyage de l’existant (pour prendre en compte la suppression d’une asso)
      for (let index = assos.length; index < assos.length + 10; index++) {
        if (this.mapAlertesMoyens.get('alertA39Asso_' + index)) {
          this.miseAJourAlerte('moyens', 'alertA39Asso_' + index, false);
        }
      }
      // Remise en place des valeurs
      for (let index = 0; index < assos.length; index++) {
        const asso = assos[index];
        this.miseAJourAlerte('moyens', 'alertA39Asso_' + index, this.alertA39Asso(asso));
      }
      // 2 - Tj
      const tj: any[] = formulaire['moyens']['lieuxTj'];
      // nettoyage de l’existant (pour prendre en compte la suppression d’un Tj)
      for (let index = tj.length; index < tj.length + 10; index++) {
        if (this.mapAlertesMoyens.get('alertA41Tj_' + index)) {
          this.miseAJourAlerte('moyens', 'alertA41Tj_' + index, false);
        }
      }
      // Remise en place des valeurs
      for (let index = 0; index < tj.length; index++) {
        const unTj = tj[index];
        this.miseAJourAlerte('moyens', 'alertA41Tj_' + index, this.alertA41Tj(unTj));
      }
      // 3 - Bav
      const bav: any[] = formulaire['moyens']['lieuxBav'];
      // nettoyage de l’existant (pour prendre en compte la suppression d’une asso)
      for (let index = bav.length; index < bav.length + 10; index++) {
        if (this.mapAlertesMoyens.get('alertA43Bav_' + index)) {
          this.miseAJourAlerte('moyens', 'alertA43Bav_' + index, false);
        }
      }
      // Remise en place des valeurs
      for (let index = 0; index < bav.length; index++) {
        const unBav = bav[index];
        this.miseAJourAlerte('moyens', 'alertA43Bav_' + index, this.alertA43Bav(unBav));
      }
      // 4 - Polgend
      const polgend: any[] = formulaire['moyens']['lieuxPolgend'];
      // nettoyage de l’existant (pour prendre en compte la suppression d’une asso)
      for (let index = polgend.length; index < polgend.length + 10; index++) {
        if (this.mapAlertesMoyens.get('alertA45Polgend_' + index)) {
          this.miseAJourAlerte('moyens', 'alertA45Polgend_' + index, false);
        }
      }
      // Remise en place des valeurs
      for (let index = 0; index < polgend.length; index++) {
        const unPolgend = polgend[index];
        this.miseAJourAlerte('moyens', 'alertA45Polgend_' + index, this.alertA45Polgend(unPolgend));
      }
      // 5 - Frserv
      const frserv: any[] = formulaire['moyens']['lieuxFrserv'];
      // nettoyage de l’existant (pour prendre en compte la suppression d’une asso)
      for (let index = frserv.length; index < frserv.length + 10; index++) {
        if (this.mapAlertesMoyens.get('alertA47Frserv_' + index)) {
          this.miseAJourAlerte('moyens', 'alertA47Frserv_' + index, false);
        }
      }
      // Remise en place des valeurs
      for (let index = 0; index < frserv.length; index++) {
        const unFrserv = frserv[index];
        this.miseAJourAlerte('moyens', 'alertA47Frserv_' + index, this.alertA47Frserv(unFrserv));
      }
      // 6 - Sad
      const sad: any[] = formulaire['moyens']['lieuxSad'];
      // nettoyage de l’existant (pour prendre en compte la suppression d’une asso)
      for (let index = sad.length; index < sad.length + 10; index++) {
        if (this.mapAlertesMoyens.get('alertA49Sad_' + index)) {
          this.miseAJourAlerte('moyens', 'alertA49Sad_' + index, false);
        }
      }
      // Remise en place des valeurs
      for (let index = 0; index < sad.length; index++) {
        const unSad = sad[index];
        this.miseAJourAlerte('moyens', 'alertA49Sad_' + index, this.alertA49Sad(unSad));
      }
      // 7 - Hosto
      const hosto: any[] = formulaire['moyens']['lieuxHosto'];
      // nettoyage de l’existant (pour prendre en compte la suppression d’une asso)
      for (let index = hosto.length; index < hosto.length + 10; index++) {
        if (this.mapAlertesMoyens.get('alertA51Hosto_' + index)) {
          this.miseAJourAlerte('moyens', 'alertA51Hosto_' + index, false);
        }
      }
      // Remise en place des valeurs
      for (let index = 0; index < hosto.length; index++) {
        const unHosto = hosto[index];
        this.miseAJourAlerte('moyens', 'alertA51Hosto_' + index, this.alertA51Hosto(unHosto));
      }
      // 8 - Colter
      const colter: any[] = formulaire['moyens']['lieuxColter'];
      // nettoyage de l’existant (pour prendre en compte la suppression d’une asso)
      for (let index = colter.length; index < colter.length + 10; index++) {
        if (this.mapAlertesMoyens.get('alertA53Colter_' + index)) {
          this.miseAJourAlerte('moyens', 'alertA53Colter_' + index, false);
        }
      }
      // Remise en place des valeurs
      for (let index = 0; index < colter.length; index++) {
        const unColter = colter[index];
        this.miseAJourAlerte('moyens', 'alertA53Colter_' + index, this.alertA53Colter(unColter));
      }
      // 9 - Autr
      const autr: any[] = formulaire['moyens']['lieuxAutr'];
      // nettoyage de l’existant (pour prendre en compte la suppression d’une asso)
      for (let index = autr.length; index < autr.length + 10; index++) {
        if (this.mapAlertesMoyens.get('alertA55Autr_' + index)) {
          this.miseAJourAlerte('moyens', 'alertA55Autr_' + index, false);
        }
      }
      // Remise en place des valeurs
      for (let index = 0; index < autr.length; index++) {
        const unAutr = autr[index];
        this.miseAJourAlerte('moyens', 'alertA55Autr_' + index, this.alertA55Autr(unAutr));
      }
    }
  }

  private alertA9(formulaire: any): boolean {
    const aaNbCdiTot = formulaire['moyens']['aaNbCdiTot'];
    const aaNbCdiAav = formulaire['moyens']['aaNbCdiAav'];
    const aaNbCdiJur = formulaire['moyens']['aaNbCdiJur'];
    const aaNbCdiPsy = formulaire['moyens']['aaNbCdiPsy'];
    const aaNbCdiWsoc = formulaire['moyens']['aaNbCdiWsoc'];
    const aaNbCdiAutrInterv = formulaire['moyens']['aaNbCdiAutrInterv'];
    const aaNbCdiDir = formulaire['moyens']['aaNbCdiDir'];
    const aaNbCdiComptsecr = formulaire['moyens']['aaNbCdiComptsecr'];
    const aaNbCdiAutrSupport = formulaire['moyens']['aaNbCdiAutrSupport'];

    return aaNbCdiTot !== null
      && aaNbCdiTot > (aaNbCdiAav + aaNbCdiJur + aaNbCdiPsy + aaNbCdiWsoc + aaNbCdiAutrInterv + aaNbCdiDir + aaNbCdiComptsecr + aaNbCdiAutrSupport);
  }

  private alertA10(formulaire: any): boolean {
    const aaEtpCdiTot = formulaire['moyens']['aaEtpCdiTot'];
    const aaEtpCdiAav = parseFloat(formulaire['moyens']['aaEtpCdiAav']) || 0;
    const aaEtpCdiJur = parseFloat(formulaire['moyens']['aaEtpCdiJur']) || 0;
    const aaEtpCdiPsy = parseFloat(formulaire['moyens']['aaEtpCdiPsy']) || 0;
    const aaEtpCdiWsoc = parseFloat(formulaire['moyens']['aaEtpCdiWsoc']) || 0;
    const aaEtpCdiAutrInterv = parseFloat(formulaire['moyens']['aaEtpCdiAutrInterv']) || 0;
    const aaEtpCdiDir = parseFloat(formulaire['moyens']['aaEtpCdiDir']) || 0;
    const aaEtpCdiComptsecr = parseFloat(formulaire['moyens']['aaEtpCdiComptsecr']) || 0;
    const aaEtpCdiAutrSupport = parseFloat(formulaire['moyens']['aaEtpCdiAutrSupport']) || 0;

    const sumAaEtpCdiTot = (aaEtpCdiAav + aaEtpCdiJur + aaEtpCdiPsy + aaEtpCdiWsoc
      + aaEtpCdiAutrInterv + aaEtpCdiDir + aaEtpCdiComptsecr + aaEtpCdiAutrSupport).toFixed(2);

    return aaEtpCdiTot !== null && aaEtpCdiTot !== '' && (parseFloat(aaEtpCdiTot) > parseFloat(sumAaEtpCdiTot));
  }

  private alertA11(formulaire: any): boolean {
    const aaNbCddTot = formulaire['moyens']['aaNbCddTot'];
    const aaNbCddAav = formulaire['moyens']['aaNbCddAav'];
    const aaNbCddJur = formulaire['moyens']['aaNbCddJur'];
    const aaNbCddPsy = formulaire['moyens']['aaNbCddPsy'];
    const aaNbCddWsoc = formulaire['moyens']['aaNbCddWsoc'];
    const aaNbCddAutrInterv = formulaire['moyens']['aaNbCddAutrInterv'];
    const aaNbCddDir = formulaire['moyens']['aaNbCddDir'];
    const aaNbCddComptsecr = formulaire['moyens']['aaNbCddComptsecr'];
    const aaNbCddAutrSupport = formulaire['moyens']['aaNbCddAutrSupport'];

    return aaNbCddTot !== null
      && aaNbCddTot > (aaNbCddAav + aaNbCddJur + aaNbCddPsy + aaNbCddWsoc + aaNbCddAutrInterv + aaNbCddDir + aaNbCddComptsecr + aaNbCddAutrSupport);
  }

  private alertA12(formulaire: any): boolean {
    const aaEtpCddTot = formulaire['moyens']['aaEtpCddTot'];
    const aaEtpCddAav = parseFloat(formulaire['moyens']['aaEtpCddAav']) || 0;
    const aaEtpCddJur = parseFloat(formulaire['moyens']['aaEtpCddJur']) || 0;
    const aaEtpCddPsy = parseFloat(formulaire['moyens']['aaEtpCddPsy']) || 0;
    const aaEtpCddWsoc = parseFloat(formulaire['moyens']['aaEtpCddWsoc']) || 0;
    const aaEtpCddAutrInterv = parseFloat(formulaire['moyens']['aaEtpCddAutrInterv']) || 0;
    const aaEtpCddDir = parseFloat(formulaire['moyens']['aaEtpCddDir']) || 0;
    const aaEtpCddComptsecr = parseFloat(formulaire['moyens']['aaEtpCddComptsecr']) || 0;
    const aaEtpCddAutrSupport = parseFloat(formulaire['moyens']['aaEtpCddAutrSupport']) || 0;

    const sumAaEtpCddTot = (aaEtpCddAav + aaEtpCddJur + aaEtpCddPsy + aaEtpCddWsoc
      + aaEtpCddAutrInterv + aaEtpCddDir + aaEtpCddComptsecr + aaEtpCddAutrSupport).toFixed(2);

    return aaEtpCddTot !== null && aaEtpCddTot !== '' && (parseFloat(aaEtpCddTot) > parseFloat(sumAaEtpCddTot));
  }

  private alertA13(formulaire: any): boolean {
    const aaNbPrestaTot = formulaire['moyens']['aaNbPrestaTot'];
    const aaNbPrestaAav = formulaire['moyens']['aaNbPrestaAav'];
    const aaNbPrestaJur = formulaire['moyens']['aaNbPrestaJur'];
    const aaNbPrestaPsy = formulaire['moyens']['aaNbPrestaPsy'];
    const aaNbPrestaWsoc = formulaire['moyens']['aaNbPrestaWsoc'];
    const aaNbPrestaAutrInterv = formulaire['moyens']['aaNbPrestaAutrInterv'];
    const aaNbPrestaDir = formulaire['moyens']['aaNbPrestaDir'];
    const aaNbPrestaComptsecr = formulaire['moyens']['aaNbPrestaComptsecr'];
    const aaNbPrestaAutrSupport = formulaire['moyens']['aaNbPrestaAutrSupport'];

    return aaNbPrestaTot !== null
      && aaNbPrestaTot > (aaNbPrestaAav + aaNbPrestaJur + aaNbPrestaPsy + aaNbPrestaWsoc + aaNbPrestaAutrInterv + aaNbPrestaDir + aaNbPrestaComptsecr + aaNbPrestaAutrSupport);
  }

  private alertA14(formulaire: any): boolean {
    const aaEtpPrestaTot = formulaire['moyens']['aaEtpPrestaTot'];
    const aaEtpPrestaAav = parseFloat(formulaire['moyens']['aaEtpPrestaAav']) || 0;
    const aaEtpPrestaJur = parseFloat(formulaire['moyens']['aaEtpPrestaJur']) || 0;
    const aaEtpPrestaPsy = parseFloat(formulaire['moyens']['aaEtpPrestaPsy']) || 0;
    const aaEtpPrestaWsoc = parseFloat(formulaire['moyens']['aaEtpPrestaWsoc']) || 0;
    const aaEtpPrestaAutrInterv = parseFloat(formulaire['moyens']['aaEtpPrestaAutrInterv']) || 0;
    const aaEtpPrestaDir = parseFloat(formulaire['moyens']['aaEtpPrestaDir']) || 0;
    const aaEtpPrestaComptsecr = parseFloat(formulaire['moyens']['aaEtpPrestaComptsecr']) || 0;
    const aaEtpPrestaAutrSupport = parseFloat(formulaire['moyens']['aaEtpPrestaAutrSupport']) || 0;

    const sumAaEtpPrestaTot = (aaEtpPrestaAav + aaEtpPrestaJur + aaEtpPrestaPsy + aaEtpPrestaWsoc
      + aaEtpPrestaAutrInterv + aaEtpPrestaDir + aaEtpPrestaComptsecr + aaEtpPrestaAutrSupport).toFixed(2);

    return aaEtpPrestaTot !== null && aaEtpPrestaTot !== '' && (parseFloat(aaEtpPrestaTot) > parseFloat(sumAaEtpPrestaTot));
  }

  private alertA15(formulaire: any): boolean {
    const aaNbBnvlTot = formulaire['moyens']['aaNbBnvlTot'];
    const aaNbBnvlAav = formulaire['moyens']['aaNbBnvlAav'];
    const aaNbBnvlJur = formulaire['moyens']['aaNbBnvlJur'];
    const aaNbBnvlPsy = formulaire['moyens']['aaNbBnvlPsy'];
    const aaNbBnvlWsoc = formulaire['moyens']['aaNbBnvlWsoc'];
    const aaNbBnvlAutrInterv = formulaire['moyens']['aaNbBnvlAutrInterv'];
    const aaNbBnvlDir = formulaire['moyens']['aaNbBnvlDir'];
    const aaNbBnvlComptsecr = formulaire['moyens']['aaNbBnvlComptsecr'];
    const aaNbBnvlAutrSupport = formulaire['moyens']['aaNbBnvlAutrSupport'];

    return aaNbBnvlTot !== null
      && aaNbBnvlTot > (aaNbBnvlAav + aaNbBnvlJur + aaNbBnvlPsy + aaNbBnvlWsoc + aaNbBnvlAutrInterv + aaNbBnvlDir + aaNbBnvlComptsecr + aaNbBnvlAutrSupport);
  }

  private alertA16(formulaire: any): boolean {
    const aaEtpBnvlTot = formulaire['moyens']['aaEtpBnvlTot'];
    const aaEtpBnvlAav = parseFloat(formulaire['moyens']['aaEtpBnvlAav']) || 0;
    const aaEtpBnvlJur = parseFloat(formulaire['moyens']['aaEtpBnvlJur']) || 0;
    const aaEtpBnvlPsy = parseFloat(formulaire['moyens']['aaEtpBnvlPsy']) || 0;
    const aaEtpBnvlWsoc = parseFloat(formulaire['moyens']['aaEtpBnvlWsoc']) || 0;
    const aaEtpBnvlAutrInterv = parseFloat(formulaire['moyens']['aaEtpBnvlAutrInterv']) || 0;
    const aaEtpBnvlDir = parseFloat(formulaire['moyens']['aaEtpBnvlDir']) || 0;
    const aaEtpBnvlComptsecr = parseFloat(formulaire['moyens']['aaEtpBnvlComptsecr']) || 0;
    const aaEtpBnvlAutrSupport = parseFloat(formulaire['moyens']['aaEtpBnvlAutrSupport']) || 0;

    const sumAaEtpBnvlTot = (aaEtpBnvlAav + aaEtpBnvlJur + aaEtpBnvlPsy + aaEtpBnvlWsoc
      + aaEtpBnvlAutrInterv + aaEtpBnvlDir + aaEtpBnvlComptsecr + aaEtpBnvlAutrSupport).toFixed(2);

    return aaEtpBnvlTot !== null && aaEtpBnvlTot !== '' && (parseFloat(aaEtpBnvlTot) > parseFloat(sumAaEtpBnvlTot));
  }

  private alertA37(formulaire: any): boolean {
    const aaFormNbJour = formulaire['moyens']['aaFormNbJour'];
    const aaFormNbPers = formulaire['moyens']['aaFormNbPers'];

    return aaFormNbJour !== null && aaFormNbJour !== '' && aaFormNbPers !== null ? (parseFloat(aaFormNbJour) / aaFormNbPers) > 20 : false;
  }

  private alertA38(formulaire: any): boolean {
    const aaFormNbJour = formulaire['moyens']['aaFormNbJour'];
    const aaFormNbPers = formulaire['moyens']['aaFormNbPers'];

    return aaFormNbJour !== null && aaFormNbJour !== '' && aaFormNbPers !== null ? (parseFloat(aaFormNbJour) / aaFormNbPers) < 0.5 : false;
  }

  private alertA57(formulaire: any): boolean {
    const aaLieuNbVictTot = formulaire['moyens']['aaLieuNbVictTot'];
    const xxRecuVict = formulaire['dg']['xxRecuVict'];

    return aaLieuNbVictTot !== null && xxRecuVict !== null ? aaLieuNbVictTot < xxRecuVict : false;
  }

  private alertA58(formulaire: any): boolean {
    const aaServiDispoUrgeAutr = formulaire['moyens']['aaServiDispoUrgeAutr'];
    const aaServiDispoUrgeAutrDet = formulaire['moyens']['aaServiDispoUrgeAutrDet'];

    return aaServiDispoUrgeAutr === '1' && (aaServiDispoUrgeAutrDet === null || aaServiDispoUrgeAutrDet === '');
  }

  private alertA59(formulaire: any): boolean {
    const aaServiDispoUrgeActiv = formulaire['moyens']['aaServiDispoUrgeActiv'];
    const aaServiDispoUrgeNbVict = formulaire['moyens']['aaServiDispoUrgeNbVict'];

    return aaServiDispoUrgeActiv === '1' && aaServiDispoUrgeNbVict === null;
  }

  private alertA39Asso(formulaire: any): boolean {
    const aaLieuAssoNbPermEtp = formulaire['aaLieuAssoNbPermEtp'];
    const aaLieuAssoNbPerm = formulaire['aaLieuAssoNbPerm'];
    // aaLieuAssoNbPermEtp = nombre total de demi-journées de permanence travaillées par an
    // nombre entier et non pas nombre décimal même s'il s'agit d'ETPT
    return aaLieuAssoNbPermEtp !== null && aaLieuAssoNbPerm !== null ? Number(aaLieuAssoNbPermEtp) > (aaLieuAssoNbPerm * 3) : false;
  }

  private alertA41Tj(formulaire: any): boolean {
    const aaLieuTjNbPermEtp = formulaire['aaLieuTjNbPermEtp'];
    const aaLieuTjNbPerm = formulaire['aaLieuTjNbPerm'];
    // aaaLieuTjNbPermEtp = nombre total de demi-journées de permanence travaillées par an
    // nombre entier et non pas nombre décimal même s'il s'agit d'ETPT
    return aaLieuTjNbPermEtp !== null && aaLieuTjNbPerm !== null ? Number(aaLieuTjNbPermEtp) > (aaLieuTjNbPerm * 3) : false;
  }

  private alertA43Bav(formulaire: any): boolean {
    const aaLieuBavNbPermEtp = formulaire['aaLieuBavNbPermEtp'];
    const aaLieuBavNbPerm = formulaire['aaLieuBavNbPerm'];
    // aaLieuBavNbPermEtp = nombre total de demi-journées de permanence travaillées par an
    // nombre entier et non pas nombre décimal même s'il s'agit d'ETPT
    return aaLieuBavNbPermEtp !== null && aaLieuBavNbPerm !== null ? Number(aaLieuBavNbPermEtp) > (aaLieuBavNbPerm * 3) : false;
  }

  private alertA45Polgend(formulaire: any): boolean {
    const aaLieuPolgendNbPermEtp = formulaire['aaLieuPolgendNbPermEtp'];
    const aaLieuPolgendNbPerm = formulaire['aaLieuPolgendNbPerm'];
    // aaLieuPolgendNbPermEtp = nombre total de demi-journées de permanence travaillées par an
    // nombre entier et non pas nombre décimal même s'il s'agit d'ETPT
    return aaLieuPolgendNbPermEtp !== null && aaLieuPolgendNbPerm !== null ? Number(aaLieuPolgendNbPermEtp) > (aaLieuPolgendNbPerm * 3) : false;
  }

  private alertA47Frserv(formulaire: any): boolean {
    const aaLieuFrservNbPermEtp = formulaire['aaLieuFrservNbPermEtp'];
    const aaLieuFrservNbPerm = formulaire['aaLieuFrservNbPerm'];
    // aaLieuFrservNbPermEtp = nombre total de demi-journées de permanence travaillées par an
    // nombre entier et non pas nombre décimal même s'il s'agit d'ETPT
    return aaLieuFrservNbPermEtp !== null && aaLieuFrservNbPerm !== null ? Number(aaLieuFrservNbPermEtp) > (aaLieuFrservNbPerm * 3) : false;
  }

  private alertA49Sad(formulaire: any): boolean {
    const aaLieuSadNbPermEtp = formulaire['aaLieuSadNbPermEtp'];
    const aaLieuSadNbPerm = formulaire['aaLieuSadNbPerm'];
    // aaLieuSadNbPermEtp = nombre total de demi-journées de permanence travaillées par an
    // nombre entier et non pas nombre décimal même s'il s'agit d'ETPT
    return aaLieuSadNbPermEtp !== null && aaLieuSadNbPerm !== null ? Number(aaLieuSadNbPermEtp) > (aaLieuSadNbPerm * 3) : false;
  }

  private alertA51Hosto(formulaire: any): boolean {
    const aaLieuHostoNbPermEtp = formulaire['aaLieuHostoNbPermEtp'];
    const aaLieuHostoNbPerm = formulaire['aaLieuHostoNbPerm'];
    // aaLieuHostoNbPermEtp = nombre total de demi-journées de permanence travaillées par an
    // nombre entier et non pas nombre décimal même s'il s'agit d'ETPT
    return aaLieuHostoNbPermEtp !== null && aaLieuHostoNbPerm !== null ? Number(aaLieuHostoNbPermEtp) > (aaLieuHostoNbPerm * 3) : false;
  }

  private alertA53Colter(formulaire: any): boolean {
    const aaLieuColterNbPermEtp = formulaire['aaLieuColterNbPermEtp'];
    const aaLieuColterNbPerm = formulaire['aaLieuColterNbPerm'];
    // aaLieuColterNbPermEtp = nombre total de demi-journées de permanence travaillées par an
    // nombre entier et non pas nombre décimal même s'il s'agit d'ETPT
    return aaLieuColterNbPermEtp !== null && aaLieuColterNbPerm !== null ? Number(aaLieuColterNbPermEtp) > (aaLieuColterNbPerm * 3) : false;
  }

  private alertA55Autr(formulaire: any): boolean {
    const aaLieuAutrNbPermEtp = formulaire['aaLieuAutrNbPermEtp'];
    const aaLieuAutrNbPerm = formulaire['aaLieuAutrNbPerm'];
    // aaLieuAutrNbPermEtp = nombre total de demi-journées de permanence travaillées par an
    // nombre entier et non pas nombre décimal même s'il s'agit d'ETPT
    return aaLieuAutrNbPermEtp !== null && aaLieuAutrNbPerm !== null ? Number(aaLieuAutrNbPermEtp) > (aaLieuAutrNbPerm * 3) : false;
  }

  // Onglet PremiersContacts 
  private testPremiersContacts(formulaire: any): void {
    this.miseAJourAlerte('premiersContacts', 'alertB1', this.alertB1(formulaire));
    this.miseAJourAlerte('premiersContacts', 'alertB2', this.alertB2(formulaire));
    this.miseAJourAlerte('premiersContacts', 'alertB3', this.alertB3(formulaire));
    this.miseAJourAlerte('premiersContacts', 'alertB4', this.alertB4(formulaire));
  }

  private alertB1(formulaire: any): boolean {
    const bbPremContactOrigTot = formulaire['premiersContacts']['bbPremContactOrigTot'];
    const bbPremContactOrigAsso = formulaire['premiersContacts']['bbPremContactOrigAsso'];
    const bbPremContactOrigPerso = formulaire['premiersContacts']['bbPremContactOrigPerso'];

    return bbPremContactOrigTot !== null && bbPremContactOrigTot > (bbPremContactOrigAsso + bbPremContactOrigPerso);
  }

  private alertB2(formulaire: any): boolean {
    const bbPremContactModeTot = formulaire['premiersContacts']['bbPremContactModeTot'];
    const bbPremContactModeEcrit = formulaire['premiersContacts']['bbPremContactModeEcrit'];
    const bbPremContactModeTel = formulaire['premiersContacts']['bbPremContactModeTel'];
    const bbPremContactModePres = formulaire['premiersContacts']['bbPremContactModePres'];

    return bbPremContactModeTot !== null && bbPremContactModeTot > (bbPremContactModeEcrit + bbPremContactModeTel + bbPremContactModePres);
  }

  private alertB3(formulaire: any): boolean {
    const bbPremContactOrientTot = formulaire['premiersContacts']['bbPremContactOrientTot'];
    const bbPremContactOrientProc = formulaire['premiersContacts']['bbPremContactOrientProc'];
    const bbPremContactOrientAutjud = formulaire['premiersContacts']['bbPremContactOrientAutjud'];
    const bbPremContactOrientPenit = formulaire['premiersContacts']['bbPremContactOrientPenit'];
    const bbPremContactOrientPolgend = formulaire['premiersContacts']['bbPremContactOrientPolgend'];
    const bbPremContactOrientUmj = formulaire['premiersContacts']['bbPremContactOrientUmj'];
    const bbPremContactOrientHosto = formulaire['premiersContacts']['bbPremContactOrientHosto'];
    const bbPremContactOrientColter = formulaire['premiersContacts']['bbPremContactOrientColter'];
    const bbPremContactOrientAsso = formulaire['premiersContacts']['bbPremContactOrientAsso'];
    const bbPremContactOrientSad = formulaire['premiersContacts']['bbPremContactOrientSad'];
    const bbPremContactOrientPftel = formulaire['premiersContacts']['bbPremContactOrientPftel'];
    const bbPremContactOrientFrvict = formulaire['premiersContacts']['bbPremContactOrientFrvict'];
    const bbPremContactOrientPart = formulaire['premiersContacts']['bbPremContactOrientPart'];
    const bbPremContactOrientPub = formulaire['premiersContacts']['bbPremContactOrientPub'];
    const bbPremContactOrientRela = formulaire['premiersContacts']['bbPremContactOrientRela'];
    const bbPremContactOrientAutr = formulaire['premiersContacts']['bbPremContactOrientAutr'];

    return bbPremContactOrientTot !== null &&
      bbPremContactOrientTot > (bbPremContactOrientProc + bbPremContactOrientAutjud + bbPremContactOrientPenit
        + bbPremContactOrientPolgend + bbPremContactOrientUmj + bbPremContactOrientHosto + bbPremContactOrientColter
        + bbPremContactOrientAsso + bbPremContactOrientSad + bbPremContactOrientPftel + bbPremContactOrientFrvict
        + bbPremContactOrientPart + bbPremContactOrientPub + bbPremContactOrientRela + bbPremContactOrientAutr);
  }

  private alertB4(formulaire: any): boolean {
    const bbPremContactOrientAutr = formulaire['premiersContacts']['bbPremContactOrientAutr'];
    const bbPremContactOrientAutrDet = formulaire['premiersContacts']['bbPremContactOrientAutrDet'];

    return bbPremContactOrientAutr !== null && bbPremContactOrientAutr > 0 && (bbPremContactOrientAutrDet === '' || bbPremContactOrientAutrDet === null);
  }

  // Onglet Personnes recues 
  private testPersonnesRecues(formulaire: any): void {
    this.miseAJourAlerte('personnesRecues', 'alertC1', this.alertC1(formulaire));
    this.miseAJourAlerte('personnesRecues', 'alertC2', this.alertC2(formulaire));
    this.miseAJourAlerte('personnesRecues', 'alertC3', this.alertC3(formulaire));
    this.miseAJourAlerte('personnesRecues', 'alertC4', this.alertC4(formulaire));
    this.miseAJourAlerte('personnesRecues', 'alertC5', this.alertC5(formulaire));
    this.miseAJourAlerte('personnesRecues', 'alertC6', this.alertC6(formulaire));
    this.miseAJourAlerte('personnesRecues', 'alertC7', this.alertC7(formulaire));
    this.miseAJourAlerte('personnesRecues', 'alertC8', this.alertC8(formulaire));
    this.miseAJourAlerte('personnesRecues', 'alertC41', this.alertC41(formulaire));
    this.miseAJourAlerte('personnesRecues', 'alertC9', this.alertC9(formulaire));
    this.miseAJourAlerte('personnesRecues', 'alertC24', this.alertC24(formulaire));
    this.miseAJourAlerte('personnesRecues', 'alertC25', this.alertC25(formulaire));
    this.miseAJourAlerte('personnesRecues', 'alertC29', this.alertC29(formulaire));
    this.miseAJourAlerte('personnesRecues', 'alertC30', this.alertC30(formulaire));
    this.miseAJourAlerte('personnesRecues', 'alertC32', this.alertC32(formulaire));
    this.miseAJourAlerte('personnesRecues', 'alertC34', this.alertC34(formulaire));
    this.miseAJourAlerte('personnesRecues', 'alertC35', this.alertC35(formulaire));
    this.miseAJourAlerte('personnesRecues', 'alertC36', this.alertC36(formulaire));
    this.miseAJourAlerte('personnesRecues', 'alertC10', this.alertC10(formulaire));
    this.miseAJourAlerte('personnesRecues', 'alertC38', this.alertC38(formulaire));
    this.miseAJourAlerte('personnesRecues', 'alertC40', this.alertC40(formulaire));
    this.miseAJourAlerte('personnesRecues', 'alertC42', this.alertC42(formulaire));


    // Alertes sur la Boucle des TJ
    const tj: any[] = formulaire['personnesRecues']['detailPersonnesRecues'];
    // nettoyage de l’existant (pour prendre en compte la suppression d’un Tj)
    for (let index = tj.length; index < tj.length + 10; index++) {
      if (this.mapAlertesPersonnesRecues.get('alertC15Tj_' + index)) {
        this.miseAJourAlerte('personnesRecues', 'alertC15Tj_' + index, false);
      }
      if (this.mapAlertesPersonnesRecues.get('alertC16Tj_' + index)) {
        this.miseAJourAlerte('personnesRecues', 'alertC16Tj_' + index, false);
      }
      if (this.mapAlertesPersonnesRecues.get('alertC18Tj_' + index)) {
        this.miseAJourAlerte('personnesRecues', 'alertC18Tj_' + index, false);
      }
      if (this.mapAlertesPersonnesRecues.get('alertC19Tj_' + index)) {
        this.miseAJourAlerte('personnesRecues', 'alertC19Tj_' + index, false);
      }
      if (this.mapAlertesPersonnesRecues.get('alertC21Tj_' + index)) {
        this.miseAJourAlerte('personnesRecues', 'alertC21Tj_' + index, false);
      }
      if (this.mapAlertesPersonnesRecues.get('alertC23Tj_' + index)) {
        this.miseAJourAlerte('personnesRecues', 'alertC23Tj_' + index, false);
      }
      if (this.mapAlertesPersonnesRecues.get('alertC27Tj_' + index)) {
        this.miseAJourAlerte('personnesRecues', 'alertC27Tj_' + index, false);
      }
    }
    // Remise en place des valeurs
    for (let index = 0; index < tj.length; index++) {
      const unTj = tj[index];
      this.miseAJourAlerte('personnesRecues', 'alertC15Tj_' + index, this.alertC15Tj(unTj));
      this.miseAJourAlerte('personnesRecues', 'alertC16Tj_' + index, this.alertC16Tj(unTj));
      this.miseAJourAlerte('personnesRecues', 'alertC18Tj_' + index, this.alertC18Tj(unTj));
      this.miseAJourAlerte('personnesRecues', 'alertC19Tj_' + index, this.alertC19Tj(unTj));
      this.miseAJourAlerte('personnesRecues', 'alertC21Tj_' + index, this.alertC21Tj(unTj));
      this.miseAJourAlerte('personnesRecues', 'alertC23Tj_' + index, this.alertC23Tj(unTj));
      this.miseAJourAlerte('personnesRecues', 'alertC27Tj_' + index, this.alertC27Tj(unTj));
    }
  }

  private alertC1(formulaire: any): boolean {
    const ccRecuNbTot = formulaire['personnesRecues']['ccRecuNbTot'];
    const ccRecuVictNb = formulaire['personnesRecues']['ccRecuVictNb'];
    const ccRecuNvictNb = formulaire['personnesRecues']['ccRecuNvictNb'];

    return ccRecuNbTot !== null && ccRecuNbTot > ccRecuVictNb + ccRecuNvictNb;
  }

  private alertC2(formulaire: any): boolean {
    const ccRecuSuiviTot = formulaire['personnesRecues']['ccRecuSuiviTot'];
    const ccRecuVictSuivi = formulaire['personnesRecues']['ccRecuVictSuivi'];
    const ccRecuNvictSuivi = formulaire['personnesRecues']['ccRecuNvictSuivi'];

    return ccRecuSuiviTot !== null && ccRecuSuiviTot > ccRecuVictSuivi + ccRecuNvictSuivi;
  }

  private alertC3(formulaire: any): boolean {
    const ccRecuEntretTot = formulaire['personnesRecues']['ccRecuEntretTot'];
    const ccRecuVictEntret = formulaire['personnesRecues']['ccRecuVictEntret'];
    const ccRecuNvictEntret = formulaire['personnesRecues']['ccRecuNvictEntret'];

    return ccRecuEntretTot !== null && ccRecuEntretTot > ccRecuVictEntret + ccRecuNvictEntret;
  }

  private alertC4(formulaire: any): boolean {
    const ccVictMinTot = formulaire['personnesRecues']['ccVictMinTot'];
    const ccVictFemMin = formulaire['personnesRecues']['ccVictFemMin'];
    const ccVictHomMin = formulaire['personnesRecues']['ccVictHomMin'];

    return ccVictMinTot !== null && ccVictMinTot > ccVictFemMin + ccVictHomMin;
  }

  private alertC5(formulaire: any): boolean {
    const ccVictMajTot = formulaire['personnesRecues']['ccVictMajTot'];
    const ccVictFemMaj = formulaire['personnesRecues']['ccVictFemMaj'];
    const ccVictHomMaj = formulaire['personnesRecues']['ccVictHomMaj'];

    return ccVictMajTot !== null && ccVictMajTot > ccVictFemMaj + ccVictHomMaj;
  }

  private alertC6(formulaire: any): boolean {
    const ccVictSeniorTot = formulaire['personnesRecues']['ccVictSeniorTot'];
    const ccVictFemSenior = formulaire['personnesRecues']['ccVictFemSenior'];
    const ccVictHomSenior = formulaire['personnesRecues']['ccVictHomSenior'];

    return ccVictSeniorTot !== null && ccVictSeniorTot > ccVictFemSenior + ccVictHomSenior;
  }

  private alertC7(formulaire: any): boolean {
    const ccVictFemTot = formulaire['personnesRecues']['ccVictFemTot'];
    const ccVictFemMin = formulaire['personnesRecues']['ccVictFemMin'];
    const ccVictFemMaj = formulaire['personnesRecues']['ccVictFemMaj'];
    const ccVictFemSenior = formulaire['personnesRecues']['ccVictFemSenior'];

    return ccVictFemTot !== null && ccVictFemTot > ccVictFemMin + ccVictFemMaj + ccVictFemSenior;
  }

  private alertC8(formulaire: any): boolean {
    const ccVictHomTot = formulaire['personnesRecues']['ccVictHomTot'];
    const ccVictHomMin = formulaire['personnesRecues']['ccVictHomMin'];
    const ccVictHomMaj = formulaire['personnesRecues']['ccVictHomMaj'];
    const ccVictHomSenior = formulaire['personnesRecues']['ccVictHomSenior'];

    return ccVictHomTot !== null && ccVictHomTot > ccVictHomMin + ccVictHomMaj + ccVictHomSenior;
  }

  private alertC41(formulaire: any): boolean {
    const ccVictPpTot = formulaire['personnesRecues']['ccVictPpTot'];
    const ccVictFemTot = formulaire['personnesRecues']['ccVictFemTot'];
    const ccVictHomTot = formulaire['personnesRecues']['ccVictHomTot'];

    return ccVictPpTot !== null && ccVictPpTot > ccVictFemTot + ccVictHomTot;
  }

  private alertC9(formulaire: any): boolean {
    const ccVictTot = formulaire['personnesRecues']['ccVictTot'];
    const ccVictFemTot = formulaire['personnesRecues']['ccVictFemTot'];
    const ccVictHomTot = formulaire['personnesRecues']['ccVictHomTot'];
    const ccVictPm = formulaire['personnesRecues']['ccVictPm'];

    return ccVictTot !== null && ccVictTot > ccVictFemTot + ccVictHomTot + ccVictPm;
  }

  private alertC24(formulaire: any): boolean {
    const ccVifVict = formulaire['personnesRecues']['ccVifVict'];
    const ccVifTgdSuiviTot = formulaire['personnesRecues']['ccVifTgdSuiviTot'];
    const ccVifBarSuiviTot = formulaire['personnesRecues']['ccVifBarSuiviTot'];
    const ccVifEvviCpleVictTot = formulaire['personnesRecues']['ccVifEvviCpleVictTot'];
    const ccVifEvviHcpleVictTot = formulaire['personnesRecues']['ccVifEvviHcpleVictTot'];

    return ccVifVict !== null && ccVifVict < ccVifTgdSuiviTot + ccVifBarSuiviTot + ccVifEvviCpleVictTot + ccVifEvviHcpleVictTot;
  }

  private alertC25(formulaire: any): boolean {
    const ccVifEntret = formulaire['personnesRecues']['ccVifEntret'];
    const ccVifTgdEntretTot = formulaire['personnesRecues']['ccVifTgdEntretTot'];
    const ccVifBarEntretTot = formulaire['personnesRecues']['ccVifBarEntretTot'];
    const ccVifEvviCpleEntretTot = formulaire['personnesRecues']['ccVifEvviCpleEntretTot'];
    const ccVifEvviHcpleEntretTot = formulaire['personnesRecues']['ccVifEvviHcpleEntretTot'];

    return ccVifEntret !== null && ccVifEntret < ccVifTgdEntretTot + ccVifBarEntretTot + ccVifEvviCpleEntretTot + ccVifEvviHcpleEntretTot;
  }

  private alertC29(formulaire: any): boolean {
    const ccMinSuivi = formulaire['personnesRecues']['ccMinSuivi'];
    const ccMinVict = formulaire['personnesRecues']['ccMinVict'];

    return ccMinSuivi !== null && ccMinVict !== null && ccMinSuivi > ccMinVict;
  }

  private alertC30(formulaire: any): boolean {
    const ccMinEntret = formulaire['personnesRecues']['ccMinEntret'];
    const ccMinVict = formulaire['personnesRecues']['ccMinVict'];

    return ccMinEntret !== null && ccMinVict !== null && ccMinEntret < ccMinVict;
  }

  private alertC32(formulaire: any): boolean {
    const ccTerroEntret = formulaire['personnesRecues']['ccTerroEntret'];
    const ccTerroVict = formulaire['personnesRecues']['ccTerroVict'];

    return ccTerroEntret !== null && ccTerroVict !== null && ccTerroEntret < ccTerroVict;
  }

  private alertC34(formulaire: any): boolean {
    const ccRestauVictPart = formulaire['personnesRecues']['ccRestauVictPart'];
    const ccRestauVictInform = formulaire['personnesRecues']['ccRestauVictInform'];

    return ccRestauVictPart !== null && ccRestauVictInform !== null && ccRestauVictPart > ccRestauVictInform;
  }

  private alertC35(formulaire: any): boolean {
    const ccRestauVictPart = formulaire['personnesRecues']['ccRestauVictPart'];
    const ccRestauMesure = formulaire['personnesRecues']['ccRestauMesure'];

    return ccRestauVictPart !== null && ccRestauMesure !== null && ccRestauVictPart < ccRestauMesure;
  }

  private alertC36(formulaire: any): boolean {
    const ccRestauVictPart = formulaire['personnesRecues']['ccRestauVictPart'];
    const ccRestauEntret = formulaire['personnesRecues']['ccRestauEntret'];

    return ccRestauVictPart !== null && ccRestauEntret !== null && ccRestauVictPart > ccRestauEntret;
  }

  private alertC10(formulaire: any): boolean {
    const ccRestauMesureTot = formulaire['personnesRecues']['ccRestauMesureTot'];
    const ccRestauMesureRdv = formulaire['personnesRecues']['ccRestauMesureRdv'];
    const ccRestauMesureRcv = formulaire['personnesRecues']['ccRestauMesureRcv'];
    const ccRestauMesureMr = formulaire['personnesRecues']['ccRestauMesureMr'];
    const ccRestauMesureCr = formulaire['personnesRecues']['ccRestauMesureCr'];
    const ccRestauMesureCercle = formulaire['personnesRecues']['ccRestauMesureCercle'];
    const ccRestauMesureCsr = formulaire['personnesRecues']['ccRestauMesureCsr'];
    const ccRestauMesureCar = formulaire['personnesRecues']['ccRestauMesureCar'];

    return ccRestauMesureTot !== null && ccRestauMesureTot > ccRestauMesureRdv + ccRestauMesureRcv + ccRestauMesureMr
      + ccRestauMesureCr + ccRestauMesureCercle + ccRestauMesureCsr + ccRestauMesureCar;
  }

  private alertC38(formulaire: any): boolean {
    const ccDispoPartAutr = formulaire['personnesRecues']['ccDispoPartAutr'];
    const ccDispoPartAutrDet = formulaire['personnesRecues']['ccDispoPartAutrDet'];

    return ccDispoPartAutr === '1' && (ccDispoPartAutrDet === '' || ccDispoPartAutrDet === null);
  }

  private alertC40(formulaire: any): boolean {
    const ccDispoPartAutrEntret = formulaire['personnesRecues']['ccDispoPartAutrEntret'];
    const ccDispoPartAutrContact = formulaire['personnesRecues']['ccDispoPartAutrContact'];

    return ccDispoPartAutrEntret !== null && ccDispoPartAutrContact !== null && ccDispoPartAutrEntret < ccDispoPartAutrContact;
  }

  private alertC42(formulaire: any): boolean {
    const ccRecuNvictEntret = formulaire['personnesRecues']['ccRecuNvictEntret'];
    const ccRecuNvictNb = formulaire['personnesRecues']['ccRecuNvictNb'];

    return ccRecuNvictEntret !== null && ccRecuNvictNb !== null && ccRecuNvictEntret < ccRecuNvictNb;
  }

  private alertC15Tj(formulaire: any): boolean {
    const ccTjVifTgdNb = formulaire['ccTjVifTgdNb'];
    const ccTjVifTgdSuivi = formulaire['ccTjVifTgdSuivi'];

    return ccTjVifTgdNb !== null && ccTjVifTgdSuivi !== null ? ccTjVifTgdNb > ccTjVifTgdSuivi : false;
  }

  private alertC16Tj(formulaire: any): boolean {
    const ccTjVifTgdNb = formulaire['ccTjVifTgdNb'];
    const ccTjVifTgdEntret = formulaire['ccTjVifTgdEntret'];

    return ccTjVifTgdNb !== null && ccTjVifTgdEntret !== null ? ccTjVifTgdNb > ccTjVifTgdEntret : false;
  }

  private alertC18Tj(formulaire: any): boolean {
    const ccTjVifBarNb = formulaire['ccTjVifBarNb'];
    const ccTjVifBarSuivi = formulaire['ccTjVifBarSuivi'];

    return ccTjVifBarNb !== null && ccTjVifBarSuivi !== null ? ccTjVifBarNb > ccTjVifBarSuivi : false;
  }

  private alertC19Tj(formulaire: any): boolean {
    const ccTjVifBarNb = formulaire['ccTjVifBarNb'];
    const ccTjVifBarEntret = formulaire['ccTjVifBarEntret'];

    return ccTjVifBarNb !== null && ccTjVifBarEntret !== null ? ccTjVifBarNb > ccTjVifBarEntret : false;
  }

  private alertC21Tj(formulaire: any): boolean {
    const ccTjVifEvviCpleEntret = formulaire['ccTjVifEvviCpleEntret'];
    const ccTjVifEvviCpleVict = formulaire['ccTjVifEvviCpleVict'];

    return ccTjVifEvviCpleEntret !== null && ccTjVifEvviCpleVict !== null ? ccTjVifEvviCpleEntret < ccTjVifEvviCpleVict : false;
  }

  private alertC23Tj(formulaire: any): boolean {
    const ccTjVifEvviHcpleEntret = formulaire['ccTjVifEvviHcpleEntret'];
    const ccTjVifEvviHcpleVict = formulaire['ccTjVifEvviHcpleVict'];

    return ccTjVifEvviHcpleEntret !== null && ccTjVifEvviHcpleVict !== null ? ccTjVifEvviHcpleEntret < ccTjVifEvviHcpleVict : false;
  }

  private alertC27Tj(formulaire: any): boolean {
    const ccTjEvviHvifEntret = formulaire['ccTjEvviHvifEntret'];
    const ccTjEvviHvifVict = formulaire['ccTjEvviHvifVict'];

    return ccTjEvviHvifEntret !== null && ccTjEvviHvifVict !== null ? ccTjEvviHvifEntret < ccTjEvviHvifVict : false;
  }

  // Onglet Contexte faits procedure
  private testContexteFaitsProcedure(formulaire: any): void {
    this.miseAJourAlerte('contexteFaitsProcedure', 'alertD1', this.alertD1(formulaire));
    this.miseAJourAlerte('contexteFaitsProcedure', 'alertD7', this.alertD7(formulaire));
    this.miseAJourAlerte('contexteFaitsProcedure', 'alertD2', this.alertD2(formulaire));
    this.miseAJourAlerte('contexteFaitsProcedure', 'alertD8', this.alertD8(formulaire));
    this.miseAJourAlerte('contexteFaitsProcedure', 'alertD3', this.alertD3(formulaire));
    this.miseAJourAlerte('contexteFaitsProcedure', 'alertD9', this.alertD9(formulaire));
    this.miseAJourAlerte('contexteFaitsProcedure', 'alertD4', this.alertD4(formulaire));
    this.miseAJourAlerte('contexteFaitsProcedure', 'alertD10', this.alertD10(formulaire));
    this.miseAJourAlerte('contexteFaitsProcedure', 'alertD5', this.alertD5(formulaire));
    this.miseAJourAlerte('contexteFaitsProcedure', 'alertD12', this.alertD12(formulaire));
    this.miseAJourAlerte('contexteFaitsProcedure', 'alertD6', this.alertD6(formulaire));
    this.miseAJourAlerte('contexteFaitsProcedure', 'alertD13', this.alertD13(formulaire));
    this.miseAJourAlerte('contexteFaitsProcedure', 'alertD14', this.alertD14(formulaire));
    this.miseAJourAlerte('contexteFaitsProcedure', 'alertD15', this.alertD15(formulaire));
  }

  private alertD1(formulaire: any): boolean {
    const ddFaitPersoTot = formulaire['contexteFaitsProcedure']['ddFaitPersoTot'];
    const ddFaitPersoHomiVolo = formulaire['contexteFaitsProcedure']['ddFaitPersoHomiVolo'];
    const ddFaitPersoSexuel = formulaire['contexteFaitsProcedure']['ddFaitPersoSexuel'];
    const ddFaitPersoProxe = formulaire['contexteFaitsProcedure']['ddFaitPersoProxe'];
    const ddFaitPersoViolen = formulaire['contexteFaitsProcedure']['ddFaitPersoViolen'];
    const ddFaitPersoHomiInvolo = formulaire['contexteFaitsProcedure']['ddFaitPersoHomiInvolo'];
    const ddFaitPersoBless = formulaire['contexteFaitsProcedure']['ddFaitPersoBless'];
    const ddFaitPersoAband = formulaire['contexteFaitsProcedure']['ddFaitPersoAband'];
    const ddFaitPersoMenace = formulaire['contexteFaitsProcedure']['ddFaitPersoMenace'];
    const ddFaitPersoDiscri = formulaire['contexteFaitsProcedure']['ddFaitPersoDiscri'];
    const ddFaitPersoAutr = formulaire['contexteFaitsProcedure']['ddFaitPersoAutr'];

    return ddFaitPersoTot !== null && ddFaitPersoTot > ddFaitPersoHomiVolo + ddFaitPersoSexuel + ddFaitPersoProxe + ddFaitPersoViolen
      + ddFaitPersoHomiInvolo + ddFaitPersoBless + ddFaitPersoAband + ddFaitPersoMenace + ddFaitPersoDiscri + ddFaitPersoAutr;
  }

  private alertD7(formulaire: any): boolean {
    const ddFaitPersoAutr = formulaire['contexteFaitsProcedure']['ddFaitPersoAutr'];
    const ddFaitPersoAutrDet = formulaire['contexteFaitsProcedure']['ddFaitPersoAutrDet'];

    return ddFaitPersoAutr !== null && ddFaitPersoAutr > 0 && (ddFaitPersoAutrDet === '' || ddFaitPersoAutrDet === null);
  }

  private alertD2(formulaire: any): boolean {
    const ddFaitBienTot = formulaire['contexteFaitsProcedure']['ddFaitBienTot'];
    const ddFaitBienVolSimp = formulaire['contexteFaitsProcedure']['ddFaitBienVolSimp'];
    const ddFaitBienVolAggr = formulaire['contexteFaitsProcedure']['ddFaitBienVolAggr'];
    const ddFaitBienEscro = formulaire['contexteFaitsProcedure']['ddFaitBienEscro'];
    const ddFaitBienDegrad = formulaire['contexteFaitsProcedure']['ddFaitBienDegrad'];
    const ddFaitBienAutr = formulaire['contexteFaitsProcedure']['ddFaitBienAutr'];

    return ddFaitBienTot !== null && ddFaitBienTot > ddFaitBienVolSimp + ddFaitBienVolAggr + ddFaitBienEscro + ddFaitBienDegrad
      + ddFaitBienAutr;
  }

  private alertD8(formulaire: any): boolean {
    const ddFaitBienAutr = formulaire['contexteFaitsProcedure']['ddFaitBienAutr'];
    const ddFaitBienAutrDet = formulaire['contexteFaitsProcedure']['ddFaitBienAutrDet'];

    return ddFaitBienAutr !== null && ddFaitBienAutr > 0 && (ddFaitBienAutrDet === '' || ddFaitBienAutrDet === null);
  }

  private alertD3(formulaire: any): boolean {
    const ddFaitCircuTot = formulaire['contexteFaitsProcedure']['ddFaitCircuTot'];
    const ddFaitCircuBless = formulaire['contexteFaitsProcedure']['ddFaitCircuBless'];
    const ddFaitCircuDegat = formulaire['contexteFaitsProcedure']['ddFaitCircuDegat'];

    return ddFaitCircuTot !== null && ddFaitCircuTot > ddFaitCircuBless + ddFaitCircuDegat;
  }

  private alertD9(formulaire: any): boolean {
    const ddFaitAutr = formulaire['contexteFaitsProcedure']['ddFaitAutr'];
    const ddFaitAutrDet = formulaire['contexteFaitsProcedure']['ddFaitAutrDet'];

    return ddFaitAutr !== null && ddFaitAutr > 0 && (ddFaitAutrDet === '' || ddFaitAutrDet === null);
  }

  private alertD4(formulaire: any): boolean {
    const ddFaitTot = formulaire['contexteFaitsProcedure']['ddFaitTot'];
    const ddFaitPersoTot = formulaire['contexteFaitsProcedure']['ddFaitPersoTot'];
    const ddFaitBienTot = formulaire['contexteFaitsProcedure']['ddFaitBienTot'];
    const ddFaitCircuTot = formulaire['contexteFaitsProcedure']['ddFaitCircuTot'];
    const ddFaitAutr = formulaire['contexteFaitsProcedure']['ddFaitAutr'];

    return ddFaitTot !== null && ddFaitTot > ddFaitPersoTot + ddFaitBienTot + ddFaitCircuTot + ddFaitAutr;
  }

  private alertD10(formulaire: any): boolean {
    const ddFaitTot = formulaire['contexteFaitsProcedure']['ddFaitTot'];
    const xxRecuVict = formulaire['dg']['xxRecuVict'];

    return ddFaitTot !== null && xxRecuVict !== null && ddFaitTot !== xxRecuVict;
  }

  private alertD5(formulaire: any): boolean {
    const ddCadrePersoTot = formulaire['contexteFaitsProcedure']['ddCadrePersoTot'];
    const ddCadrePersoCple = formulaire['contexteFaitsProcedure']['ddCadrePersoCple'];
    const ddCadrePersoFam = formulaire['contexteFaitsProcedure']['ddCadrePersoFam'];
    const ddCadrePersoPro = formulaire['contexteFaitsProcedure']['ddCadrePersoPro'];
    const ddCadrePersoScol = formulaire['contexteFaitsProcedure']['ddCadrePersoScol'];
    const ddCadrePersoSpor = formulaire['contexteFaitsProcedure']['ddCadrePersoSpor'];
    const ddCadrePersoRue = formulaire['contexteFaitsProcedure']['ddCadrePersoRue'];
    const ddCadrePersoAutr = formulaire['contexteFaitsProcedure']['ddCadrePersoAutr'];

    return ddCadrePersoTot !== null && ddCadrePersoTot > ddCadrePersoCple + ddCadrePersoFam + ddCadrePersoPro + ddCadrePersoScol
      + ddCadrePersoSpor + ddCadrePersoRue + ddCadrePersoAutr;
  }

  private alertD12(formulaire: any): boolean {
    const ddCadrePersoTot = formulaire['contexteFaitsProcedure']['ddCadrePersoTot'];
    const ddFaitPersoTot = formulaire['contexteFaitsProcedure']['ddFaitPersoTot'];

    return ddCadrePersoTot !== null && ddFaitPersoTot !== null && ddCadrePersoTot !== ddFaitPersoTot;
  }

  private alertD6(formulaire: any): boolean {
    const ddCadreBienTot = formulaire['contexteFaitsProcedure']['ddCadreBienTot'];
    const ddCadreBienCple = formulaire['contexteFaitsProcedure']['ddCadreBienCple'];
    const ddCadreBienFam = formulaire['contexteFaitsProcedure']['ddCadreBienFam'];
    const ddCadreBienPro = formulaire['contexteFaitsProcedure']['ddCadreBienPro'];
    const ddCadreBienScol = formulaire['contexteFaitsProcedure']['ddCadreBienScol'];
    const ddCadreBienSpor = formulaire['contexteFaitsProcedure']['ddCadreBienSpor'];
    const ddCadreBienRue = formulaire['contexteFaitsProcedure']['ddCadreBienRue'];
    const ddCadreBienAutr = formulaire['contexteFaitsProcedure']['ddCadreBienAutr'];

    return ddCadreBienTot !== null && ddCadreBienTot > ddCadreBienCple + ddCadreBienFam + ddCadreBienPro + ddCadreBienScol
      + ddCadreBienSpor + ddCadreBienRue + ddCadreBienAutr;
  }

  private alertD13(formulaire: any): boolean {
    const ddCadreBienTot = formulaire['contexteFaitsProcedure']['ddCadreBienTot'];
    const ddFaitBienTot = formulaire['contexteFaitsProcedure']['ddFaitBienTot'];

    return ddCadreBienTot !== null && ddFaitBienTot !== null && ddCadreBienTot !== ddFaitBienTot;
  }

  private alertD14(formulaire: any): boolean {
    const ddCadrePersoAutr = formulaire['contexteFaitsProcedure']['ddCadrePersoAutr'];
    const ddCadrePersoAutrDet = formulaire['contexteFaitsProcedure']['ddCadrePersoAutrDet'];

    return ddCadrePersoAutr !== null && ddCadrePersoAutr > 0 && (ddCadrePersoAutrDet === '' || ddCadrePersoAutrDet === null);
  }

  private alertD15(formulaire: any): boolean {
    const ddCadreBienAutr = formulaire['contexteFaitsProcedure']['ddCadreBienAutr'];
    const ddCadreBienAutrDet = formulaire['contexteFaitsProcedure']['ddCadreBienAutrDet'];

    return ddCadreBienAutr !== null && ddCadreBienAutr > 0 && (ddCadreBienAutrDet === '' || ddCadreBienAutrDet === null);
  }

  // Onglet Aides apportées
  private testAidesApportees(formulaire: any): void {
    this.miseAJourAlerte('aidesApportees', 'alertE1', this.alertE1(formulaire));
    this.miseAJourAlerte('aidesApportees', 'alertE3', this.alertE3(formulaire));
    this.miseAJourAlerte('aidesApportees', 'alertE2', this.alertE2(formulaire));
    this.miseAJourAlerte('aidesApportees', 'alertE4', this.alertE4(formulaire));
  }

  private alertE1(formulaire: any): boolean {
    const eeEntretTot = formulaire['aidesApportees']['eeEntretTot'];
    const eeEntretPsy = formulaire['aidesApportees']['eeEntretPsy'];
    const eeEntretJur = formulaire['aidesApportees']['eeEntretJur'];
    const eeEntretSoc = formulaire['aidesApportees']['eeEntretSoc'];
    const eeEntretEcout = formulaire['aidesApportees']['eeEntretEcout'];
    const eeEntretAutr = formulaire['aidesApportees']['eeEntretAutr'];

    return eeEntretTot !== null && eeEntretTot > eeEntretPsy + eeEntretJur + eeEntretSoc + eeEntretEcout
      + eeEntretAutr;
  }

  private alertE3(formulaire: any): boolean {
    const eeEntretAutr = formulaire['aidesApportees']['eeEntretAutr'];
    const eeEntretAutrDet = formulaire['aidesApportees']['eeEntretAutrDet'];

    return eeEntretAutr !== null && eeEntretAutr > 0 && (eeEntretAutrDet === '' || eeEntretAutrDet === null);
  }

  private alertE2(formulaire: any): boolean {
    const eeDiliTot = formulaire['aidesApportees']['eeDiliTot'];
    const eeDiliJur = formulaire['aidesApportees']['eeDiliJur'];
    const eeDiliCivi = formulaire['aidesApportees']['eeDiliCivi'];
    const eeDiliFondGaran = formulaire['aidesApportees']['eeDiliFondGaran'];
    const eeDiliSarvi = formulaire['aidesApportees']['eeDiliSarvi'];
    const eeDiliPenit = formulaire['aidesApportees']['eeDiliPenit'];
    const eeDiliPolgend = formulaire['aidesApportees']['eeDiliPolgend'];
    const eeDiliAvoc = formulaire['aidesApportees']['eeDiliAvoc'];
    const eeDiliHuis = formulaire['aidesApportees']['eeDiliHuis'];
    const eeDiliOrgaSoc = formulaire['aidesApportees']['eeDiliOrgaSoc'];
    const eeDiliColter = formulaire['aidesApportees']['eeDiliColter'];
    const eeDiliPref = formulaire['aidesApportees']['eeDiliPref'];
    const eeDiliOrgaLogt = formulaire['aidesApportees']['eeDiliOrgaLogt'];
    const eeDiliHebgt = formulaire['aidesApportees']['eeDiliHebgt'];
    const eeDiliHosto = formulaire['aidesApportees']['eeDiliHosto'];
    const eeDiliAsso = formulaire['aidesApportees']['eeDiliAsso'];
    const eeDiliAssur = formulaire['aidesApportees']['eeDiliAssur'];
    const eeDiliSad = formulaire['aidesApportees']['eeDiliSad'];
    const eeDiliFrserv = formulaire['aidesApportees']['eeDiliFrserv'];
    const eeDiliAutr = formulaire['aidesApportees']['eeDiliAutr'];


    return eeDiliTot !== null && eeDiliTot > eeDiliJur + eeDiliCivi + eeDiliFondGaran + eeDiliSarvi
      + eeDiliPenit + eeDiliPolgend + eeDiliAvoc + eeDiliHuis + eeDiliOrgaSoc + eeDiliColter + eeDiliPref
      + eeDiliOrgaLogt + eeDiliHebgt + eeDiliHosto + eeDiliAsso + eeDiliAssur + eeDiliSad
      + eeDiliFrserv + eeDiliAutr;
  }

  private alertE4(formulaire: any): boolean {
    const eeDiliAutr = formulaire['aidesApportees']['eeDiliAutr'];
    const eeDiliAutrDet = formulaire['aidesApportees']['eeDiliAutrDet'];

    return eeDiliAutr !== null && eeDiliAutr > 0 && (eeDiliAutrDet === '' || eeDiliAutrDet === null);
  }

  /* Onglet BAV : attention boucle */
  private testBav(formulaire: any): void {
    if (formulaire) {
      // Alertes sur Boucles pour les BAV
      if (formulaire['bav']) {
        const bav: any[] = formulaire['bav']['detailBavs'];
        // nettoyage de l’existant (pour prendre en compte la suppression d’un bav)
        for (let index = bav.length; index < bav.length + 10; index++) {
          if (this.mapAlertesBav.get('alertF1Bav_' + index)) {
            this.miseAJourAlerte('bav', 'alertF1Bav_' + index, false);
          }
          if (this.mapAlertesBav.get('alertF2Bav_' + index)) {
            this.miseAJourAlerte('bav', 'alertF2Bav_' + index, false);
          }
          if (this.mapAlertesBav.get('alertF49Bav_' + index)) {
            this.miseAJourAlerte('bav', 'alertF49Bav_' + index, false);
          }
          if (this.mapAlertesBav.get('alertF3Bav_' + index)) {
            this.miseAJourAlerte('bav', 'alertF3Bav_' + index, false);
          }
          if (this.mapAlertesBav.get('alertF4Bav_' + index)) {
            this.miseAJourAlerte('bav', 'alertF4Bav_' + index, false);
          }
          if (this.mapAlertesBav.get('alertF5Bav_' + index)) {
            this.miseAJourAlerte('bav', 'alertF5Bav_' + index, false);
          }
          if (this.mapAlertesBav.get('alertF6Bav_' + index)) {
            this.miseAJourAlerte('bav', 'alertF6Bav_' + index, false);
          }
          if (this.mapAlertesBav.get('alertF7Bav_' + index)) {
            this.miseAJourAlerte('bav', 'alertF7Bav_' + index, false);
          }
          if (this.mapAlertesBav.get('alertF8Bav_' + index)) {
            this.miseAJourAlerte('bav', 'alertF8Bav_' + index, false);
          }
          if (this.mapAlertesBav.get('alertF26Bav_' + index)) {
            this.miseAJourAlerte('bav', 'alertF26Bav_' + index, false);
          }
          if (this.mapAlertesBav.get('alertF9Bav_' + index)) {
            this.miseAJourAlerte('bav', 'alertF9Bav_' + index, false);
          }
          if (this.mapAlertesBav.get('alertF27Bav_' + index)) {
            this.miseAJourAlerte('bav', 'alertF27Bav_' + index, false);
          }
          if (this.mapAlertesBav.get('alertF10Bav_' + index)) {
            this.miseAJourAlerte('bav', 'alertF10Bav_' + index, false);
          }
          if (this.mapAlertesBav.get('alertF28Bav_' + index)) {
            this.miseAJourAlerte('bav', 'alertF28Bav_' + index, false);
          }
          if (this.mapAlertesBav.get('alertF11Bav_' + index)) {
            this.miseAJourAlerte('bav', 'alertF11Bav_' + index, false);
          }
          if (this.mapAlertesBav.get('alertF12Bav_' + index)) {
            this.miseAJourAlerte('bav', 'alertF12Bav_' + index, false);
          }
          if (this.mapAlertesBav.get('alertF30Bav_' + index)) {
            this.miseAJourAlerte('bav', 'alertF30Bav_' + index, false);
          }
          if (this.mapAlertesBav.get('alertF13Bav_' + index)) {
            this.miseAJourAlerte('bav', 'alertF13Bav_' + index, false);
          }
          if (this.mapAlertesBav.get('alertF32Bav_' + index)) {
            this.miseAJourAlerte('bav', 'alertF32Bav_' + index, false);
          }
          if (this.mapAlertesBav.get('alertF14Bav_' + index)) {
            this.miseAJourAlerte('bav', 'alertF14Bav_' + index, false);
          }
          if (this.mapAlertesBav.get('alertF40Bav_' + index)) {
            this.miseAJourAlerte('bav', 'alertF40Bav_' + index, false);
          }
          if (this.mapAlertesBav.get('alertF15Bav_' + index)) {
            this.miseAJourAlerte('bav', 'alertF15Bav_' + index, false);
          }
          if (this.mapAlertesBav.get('alertF41Bav_' + index)) {
            this.miseAJourAlerte('bav', 'alertF41Bav_' + index, false);
          }
          if (this.mapAlertesBav.get('alertF16Bav_' + index)) {
            this.miseAJourAlerte('bav', 'alertF16Bav_' + index, false);
          }
          if (this.mapAlertesBav.get('alertF42Bav_' + index)) {
            this.miseAJourAlerte('bav', 'alertF42Bav_' + index, false);
          }
          if (this.mapAlertesBav.get('alertF17Bav_' + index)) {
            this.miseAJourAlerte('bav', 'alertF17Bav_' + index, false);
          }
          if (this.mapAlertesBav.get('alertF43Bav_' + index)) {
            this.miseAJourAlerte('bav', 'alertF43Bav_' + index, false);
          }
          if (this.mapAlertesBav.get('alertF18Bav_' + index)) {
            this.miseAJourAlerte('bav', 'alertF18Bav_' + index, false);
          }
          if (this.mapAlertesBav.get('alertF44Bav_' + index)) {
            this.miseAJourAlerte('bav', 'alertF44Bav_' + index, false);
          }
          if (this.mapAlertesBav.get('alertF19Bav_' + index)) {
            this.miseAJourAlerte('bav', 'alertF19Bav_' + index, false);
          }
          if (this.mapAlertesBav.get('alertF45Bav_' + index)) {
            this.miseAJourAlerte('bav', 'alertF45Bav_' + index, false);
          }
          if (this.mapAlertesBav.get('alertF20Bav_' + index)) {
            this.miseAJourAlerte('bav', 'alertF20Bav_' + index, false);
          }
          if (this.mapAlertesBav.get('alertF48Bav_' + index)) {
            this.miseAJourAlerte('bav', 'alertF48Bav_' + index, false);
          }
        }
        // Remise en place des valeurs
        for (let index = 0; index < bav.length; index++) {
          const unBav = bav[index];
          this.miseAJourAlerte('bav', 'alertF1Bav_' + index, this.alertF1Bav(unBav));
          this.miseAJourAlerte('bav', 'alertF2Bav_' + index, this.alertF2Bav(unBav));
          this.miseAJourAlerte('bav', 'alertF49Bav_' + index, this.alertF49Bav(unBav));
          this.miseAJourAlerte('bav', 'alertF3Bav_' + index, this.alertF3Bav(unBav));
          this.miseAJourAlerte('bav', 'alertF4Bav_' + index, this.alertF4Bav(unBav));
          this.miseAJourAlerte('bav', 'alertF5Bav_' + index, this.alertF5Bav(unBav));
          this.miseAJourAlerte('bav', 'alertF6Bav_' + index, this.alertF6Bav(unBav));
          this.miseAJourAlerte('bav', 'alertF7Bav_' + index, this.alertF7Bav(unBav));
          this.miseAJourAlerte('bav', 'alertF8Bav_' + index, this.alertF8Bav(unBav));
          this.miseAJourAlerte('bav', 'alertF26Bav_' + index, this.alertF26Bav(unBav));
          this.miseAJourAlerte('bav', 'alertF9Bav_' + index, this.alertF9Bav(unBav));
          this.miseAJourAlerte('bav', 'alertF27Bav_' + index, this.alertF27Bav(unBav));
          this.miseAJourAlerte('bav', 'alertF10Bav_' + index, this.alertF10Bav(unBav));
          this.miseAJourAlerte('bav', 'alertF28Bav_' + index, this.alertF28Bav(unBav));
          this.miseAJourAlerte('bav', 'alertF11Bav_' + index, this.alertF11Bav(unBav));
          this.miseAJourAlerte('bav', 'alertF12Bav_' + index, this.alertF12Bav(unBav));
          this.miseAJourAlerte('bav', 'alertF30Bav_' + index, this.alertF30Bav(unBav));
          this.miseAJourAlerte('bav', 'alertF13Bav_' + index, this.alertF13Bav(unBav));
          this.miseAJourAlerte('bav', 'alertF32Bav_' + index, this.alertF32Bav(unBav));
          this.miseAJourAlerte('bav', 'alertF14Bav_' + index, this.alertF14Bav(unBav));
          this.miseAJourAlerte('bav', 'alertF40Bav_' + index, this.alertF40Bav(unBav));
          this.miseAJourAlerte('bav', 'alertF15Bav_' + index, this.alertF15Bav(unBav));
          this.miseAJourAlerte('bav', 'alertF41Bav_' + index, this.alertF41Bav(unBav));
          this.miseAJourAlerte('bav', 'alertF16Bav_' + index, this.alertF16Bav(unBav));
          this.miseAJourAlerte('bav', 'alertF42Bav_' + index, this.alertF42Bav(unBav));
          this.miseAJourAlerte('bav', 'alertF17Bav_' + index, this.alertF17Bav(unBav));
          this.miseAJourAlerte('bav', 'alertF43Bav_' + index, this.alertF43Bav(unBav));
          this.miseAJourAlerte('bav', 'alertF18Bav_' + index, this.alertF18Bav(unBav));
          this.miseAJourAlerte('bav', 'alertF44Bav_' + index, this.alertF44Bav(unBav));
          this.miseAJourAlerte('bav', 'alertF19Bav_' + index, this.alertF19Bav(unBav));
          this.miseAJourAlerte('bav', 'alertF45Bav_' + index, this.alertF45Bav(unBav));
          this.miseAJourAlerte('bav', 'alertF20Bav_' + index, this.alertF20Bav(unBav));
          this.miseAJourAlerte('bav', 'alertF48Bav_' + index, this.alertF48Bav(unBav));
        }
      }
    }
  }

  private alertF1Bav(formulaire: any): boolean {
    const bavAaNbTot = formulaire['bavAaNbTot'];
    const bavAaNbAav = formulaire['bavAaNbAav'];
    const bavAaNbJur = formulaire['bavAaNbJur'];
    const bavAaNbPsy = formulaire['bavAaNbPsy'];
    const bavAaNbWsoc = formulaire['bavAaNbWsoc'];
    const bavAaNbAutrInterv = formulaire['bavAaNbAutrInterv'];
    const bavAaNbDir = formulaire['bavAaNbDir'];
    const bavAaNbComptsecr = formulaire['bavAaNbComptsecr'];
    const bavAaNbAutrSupport = formulaire['bavAaNbAutrSupport'];

    return bavAaNbTot !== null && bavAaNbTot > bavAaNbAav + bavAaNbJur + bavAaNbPsy + bavAaNbWsoc + bavAaNbAutrInterv + bavAaNbDir
      + bavAaNbComptsecr + bavAaNbAutrSupport;
  }

  private alertF2Bav(formulaire: any): boolean {
    const bavAaEtpTot = formulaire['bavAaEtpTot'];
    const bavAaEtpAav = parseFloat(formulaire['bavAaEtpAav']) || 0;
    const bavAaEtpJur = parseFloat(formulaire['bavAaEtpJur']) || 0;
    const bavAaEtpPsy = parseFloat(formulaire['bavAaEtpPsy']) || 0;
    const bavAaEtpWsoc = parseFloat(formulaire['bavAaEtpWsoc']) || 0;
    const bavAaEtpAutrInterv = parseFloat(formulaire['bavAaEtpAutrInterv']) || 0;
    const bavAaEtpDir = parseFloat(formulaire['bavAaEtpDir']) || 0;
    const bavAaEtpComptsecr = parseFloat(formulaire['bavAaEtpComptsecr']) || 0;
    const bavAaEtpAutrSupport = parseFloat(formulaire['bavAaEtpAutrSupport']) || 0;

    const sumBavAaEtpTot = (bavAaEtpAav + bavAaEtpJur + bavAaEtpPsy + bavAaEtpWsoc
      + bavAaEtpAutrInterv + bavAaEtpDir + bavAaEtpComptsecr + bavAaEtpAutrSupport).toFixed(2);

    return bavAaEtpTot !== null && bavAaEtpTot !== '' && (parseFloat(bavAaEtpTot) > parseFloat(sumBavAaEtpTot));
  }

  private alertF49Bav(formulaire: any): boolean {
    const bavAaNbTot = formulaire['bavAaNbTot'];
    const bavAaEtpTot = formulaire['bavAaEtpTot'];

    return bavAaNbTot !== null && bavAaEtpTot !== null && bavAaEtpTot !== '' ? parseFloat(bavAaEtpTot) > (bavAaNbTot * 1.5) : false;
  }

  private alertF3Bav(formulaire: any): boolean {
    const bavBbPremContactOrigTot = formulaire['bavBbPremContactOrigTot'];
    const bavBbPremContactOrigAsso = formulaire['bavBbPremContactOrigAsso'];
    const bavBbPremContactOrigPerso = formulaire['bavBbPremContactOrigPerso'];

    return bavBbPremContactOrigTot !== null && bavBbPremContactOrigTot > bavBbPremContactOrigAsso + bavBbPremContactOrigPerso;
  }

  private alertF4Bav(formulaire: any): boolean {
    const bavBbPremContactModeTot = formulaire['bavBbPremContactModeTot'];
    const bavBbPremContactModeEcrit = formulaire['bavBbPremContactModeEcrit'];
    const bavBbPremContactModeTel = formulaire['bavBbPremContactModeTel'];
    const bavBbPremContactModePres = formulaire['bavBbPremContactModePres'];

    return bavBbPremContactModeTot !== null && bavBbPremContactModeTot > bavBbPremContactModeEcrit + bavBbPremContactModeTel + bavBbPremContactModePres;
  }

  private alertF5Bav(formulaire: any): boolean {
    const bavCcRecuNbTot = formulaire['bavCcRecuNbTot'];
    const bavCcRecuVictNb = formulaire['bavCcRecuVictNb'];
    const bavCcRecuNvictNb = formulaire['bavCcRecuNvictNb'];

    return bavCcRecuNbTot !== null && bavCcRecuNbTot > bavCcRecuVictNb + bavCcRecuNvictNb;
  }

  private alertF6Bav(formulaire: any): boolean {
    const bavCcRecuSuiviTot = formulaire['bavCcRecuSuiviTot'];
    const bavCcRecuVictSuivi = formulaire['bavCcRecuVictSuivi'];
    const bavCcRecuNvictSuivi = formulaire['bavCcRecuNvictSuivi'];

    return bavCcRecuSuiviTot !== null && bavCcRecuSuiviTot > bavCcRecuVictSuivi + bavCcRecuNvictSuivi;
  }

  private alertF7Bav(formulaire: any): boolean {
    const bavCcRecuEntretTot = formulaire['bavCcRecuEntretTot'];
    const bavCcRecuVictEntret = formulaire['bavCcRecuVictEntret'];
    const bavCcRecuNvictEntret = formulaire['bavCcRecuNvictEntret'];

    return bavCcRecuEntretTot !== null && bavCcRecuEntretTot > bavCcRecuVictEntret + bavCcRecuNvictEntret;
  }

  private alertF8Bav(formulaire: any): boolean {
    const bavDdFaitPersoTot = formulaire['bavDdFaitPersoTot'];
    const bavDdFaitPersoHomiVolo = formulaire['bavDdFaitPersoHomiVolo'];
    const bavDdFaitPersoSexuel = formulaire['bavDdFaitPersoSexuel'];
    const bavDdFaitPersoProxe = formulaire['bavDdFaitPersoProxe'];
    const bavDdFaitPersoViolen = formulaire['bavDdFaitPersoViolen'];
    const bavDdFaitPersoHomiInvolo = formulaire['bavDdFaitPersoHomiInvolo'];
    const bavDdFaitPersoBless = formulaire['bavDdFaitPersoBless'];
    const bavDdFaitPersoAband = formulaire['bavDdFaitPersoAband'];
    const bavDdFaitPersoMenace = formulaire['bavDdFaitPersoMenace'];
    const bavDdFaitPersoDiscri = formulaire['bavDdFaitPersoDiscri'];
    const bavDdFaitPersoAutr = formulaire['bavDdFaitPersoAutr'];

    return bavDdFaitPersoTot !== null && bavDdFaitPersoTot > bavDdFaitPersoHomiVolo + bavDdFaitPersoSexuel + bavDdFaitPersoProxe
      + bavDdFaitPersoViolen + bavDdFaitPersoHomiInvolo + bavDdFaitPersoBless + bavDdFaitPersoAband + bavDdFaitPersoMenace
      + bavDdFaitPersoDiscri + bavDdFaitPersoAutr;
  }

  private alertF26Bav(formulaire: any): boolean {
    const bavDdFaitPersoAutr = formulaire['bavDdFaitPersoAutr'];
    const bavDdFaitPersoAutrDet = formulaire['bavDdFaitPersoAutrDet'];

    return bavDdFaitPersoAutr !== null && bavDdFaitPersoAutr > 0 && (bavDdFaitPersoAutrDet === '' || bavDdFaitPersoAutrDet === null);
  }

  private alertF9Bav(formulaire: any): boolean {
    const bavDdFaitBienTot = formulaire['bavDdFaitBienTot'];
    const bavDdFaitBienVolSimp = formulaire['bavDdFaitBienVolSimp'];
    const bavDdFaitBienVolAggr = formulaire['bavDdFaitBienVolAggr'];
    const bavDdFaitBienEscro = formulaire['bavDdFaitBienEscro'];
    const bavDdFaitBienDegrad = formulaire['bavDdFaitBienDegrad'];
    const bavDdFaitBienAutr = formulaire['bavDdFaitBienAutr'];

    return bavDdFaitBienTot !== null && bavDdFaitBienTot > bavDdFaitBienVolSimp + bavDdFaitBienVolAggr + bavDdFaitBienEscro
      + bavDdFaitBienDegrad + bavDdFaitBienAutr;
  }

  private alertF27Bav(formulaire: any): boolean {
    const bavDdFaitBienAutr = formulaire['bavDdFaitBienAutr'];
    const bavDdFaitBienAutrDet = formulaire['bavDdFaitBienAutrDet'];

    return bavDdFaitBienAutr !== null && bavDdFaitBienAutr > 0 && (bavDdFaitBienAutrDet === '' || bavDdFaitBienAutrDet === null);
  }

  private alertF10Bav(formulaire: any): boolean {
    const bavDdFaitCircuTot = formulaire['bavDdFaitCircuTot'];
    const bavDdFaitCircuBless = formulaire['bavDdFaitCircuBless'];
    const bavDdFaitCircuDegat = formulaire['bavDdFaitCircuDegat'];

    return bavDdFaitCircuTot !== null && bavDdFaitCircuTot > bavDdFaitCircuBless + bavDdFaitCircuDegat;
  }

  private alertF28Bav(formulaire: any): boolean {
    const bavDdFaitAutr = formulaire['bavDdFaitAutr'];
    const bavDdFaitAutrDet = formulaire['bavDdFaitAutrDet'];

    return bavDdFaitAutr !== null && bavDdFaitAutr > 0 && (bavDdFaitAutrDet === '' || bavDdFaitAutrDet === null);
  }

  private alertF11Bav(formulaire: any): boolean {
    const bavDdFaitTot = formulaire['bavDdFaitTot'];
    const bavDdFaitPersoTot = formulaire['bavDdFaitPersoTot'];
    const bavDdFaitBienTot = formulaire['bavDdFaitBienTot'];
    const bavDdFaitCircuTot = formulaire['bavDdFaitCircuTot'];
    const bavDdFaitAutr = formulaire['bavDdFaitAutr'];

    return bavDdFaitTot !== null && bavDdFaitTot > bavDdFaitPersoTot + bavDdFaitBienTot + bavDdFaitCircuTot + bavDdFaitAutr;
  }

  private alertF12Bav(formulaire: any): boolean {
    const bavEeEntretTot = formulaire['bavEeEntretTot'];
    const bavEeEntretPsy = formulaire['bavEeEntretPsy'];
    const bavEeEntretJur = formulaire['bavEeEntretJur'];
    const bavEeEntretSoc = formulaire['bavEeEntretSoc'];
    const bavEeEntretEcout = formulaire['bavEeEntretEcout'];
    const bavEeEntretAutr = formulaire['bavEeEntretAutr'];

    return bavEeEntretTot !== null && bavEeEntretTot > bavEeEntretPsy + bavEeEntretJur + bavEeEntretSoc + bavEeEntretEcout + bavEeEntretAutr;
  }

  private alertF30Bav(formulaire: any): boolean {
    const bavEeEntretAutr = formulaire['bavEeEntretAutr'];
    const bavEeEntretAutrDet = formulaire['bavEeEntretAutrDet'];

    return bavEeEntretAutr !== null && bavEeEntretAutr > 0 && (bavEeEntretAutrDet === '' || bavEeEntretAutrDet === null);
  }

  private alertF13Bav(formulaire: any): boolean {
    const bavEeDiliTot = formulaire['bavEeDiliTot'];
    const bavEeDiliJur = formulaire['bavEeDiliJur'];
    const bavEeDiliCivi = formulaire['bavEeDiliCivi'];
    const bavEeDiliFondGaran = formulaire['bavEeDiliFondGaran'];
    const bavEeDiliSarvi = formulaire['bavEeDiliSarvi'];
    const bavEeDiliPenit = formulaire['bavEeDiliPenit'];
    const bavEeDiliPolgend = formulaire['bavEeDiliPolgend'];
    const bavEeDiliAvoc = formulaire['bavEeDiliAvoc'];
    const bavEeDiliHuis = formulaire['bavEeDiliHuis'];
    const bavEeDiliOrgaSoc = formulaire['bavEeDiliOrgaSoc'];
    const bavEeDiliColter = formulaire['bavEeDiliColter'];
    const bavEeDiliPref = formulaire['bavEeDiliPref'];
    const bavEeDiliOrgaLogt = formulaire['bavEeDiliOrgaLogt'];
    const bavEeDiliHebgt = formulaire['bavEeDiliHebgt'];
    const bavEeDiliHosto = formulaire['bavEeDiliHosto'];
    const bavEeDiliAsso = formulaire['bavEeDiliAsso'];
    const bavEeDiliAssur = formulaire['bavEeDiliAssur'];
    const bavEeDiliSad = formulaire['bavEeDiliSad'];
    const bavEeDiliFrserv = formulaire['bavEeDiliFrserv'];
    const bavEeDiliAutr = formulaire['bavEeDiliAutr'];

    return bavEeDiliTot !== null && bavEeDiliTot > bavEeDiliJur + bavEeDiliCivi + bavEeDiliFondGaran + bavEeDiliSarvi + bavEeDiliPenit
      + bavEeDiliPolgend + bavEeDiliAvoc + bavEeDiliHuis + bavEeDiliOrgaSoc + bavEeDiliColter + bavEeDiliPref + bavEeDiliOrgaLogt
      + bavEeDiliHebgt + bavEeDiliHosto + bavEeDiliAsso + bavEeDiliAssur + bavEeDiliSad + bavEeDiliFrserv + bavEeDiliAutr;
  }

  private alertF32Bav(formulaire: any): boolean {
    const bavEeDiliAutr = formulaire['bavEeDiliAutr'];
    const bavEeDiliAutrDet = formulaire['bavEeDiliAutrDet'];

    return bavEeDiliAutr !== null && bavEeDiliAutr > 0 && (bavEeDiliAutrDet === '' || bavEeDiliAutrDet === null);
  }

  private alertF14Bav(formulaire: any): boolean {
    const bavEeProcPreSentenTot = formulaire['bavEeProcPreSentenTot'];
    const bavEeProcPreSentenPlainte = formulaire['bavEeProcPreSentenPlainte'];
    const bavEeProcPreSentenEnq = formulaire['bavEeProcPreSentenEnq'];
    const bavEeProcPreSentenCss = formulaire['bavEeProcPreSentenCss'];
    const bavEeProcPreSentenPc = formulaire['bavEeProcPreSentenPc'];
    const bavEeProcPreSentenAud = formulaire['bavEeProcPreSentenAud'];
    const bavEeProcPreSentenAutr = formulaire['bavEeProcPreSentenAutr'];

    return bavEeProcPreSentenTot !== null && bavEeProcPreSentenTot > bavEeProcPreSentenPlainte + bavEeProcPreSentenEnq + bavEeProcPreSentenCss
      + bavEeProcPreSentenPc + bavEeProcPreSentenAud + bavEeProcPreSentenAutr;
  }

  private alertF40Bav(formulaire: any): boolean {
    const bavEeProcPreSentenAutr = formulaire['bavEeProcPreSentenAutr'];
    const bavEeProcPreSentenAutrDet = formulaire['bavEeProcPreSentenAutrDet'];

    return bavEeProcPreSentenAutr !== null && bavEeProcPreSentenAutr > 0 && (bavEeProcPreSentenAutrDet === '' || bavEeProcPreSentenAutrDet === null);
  }

  private alertF15Bav(formulaire: any): boolean {
    const bavEeProcPostSentenTot = formulaire['bavEeProcPostSentenTot'];
    const bavEeProcPostSentenDec = formulaire['bavEeProcPostSentenDec'];
    const bavEeProcPostSentenDef = formulaire['bavEeProcPostSentenDef'];
    const bavEeProcPostSentenRecour = formulaire['bavEeProcPostSentenRecour'];
    const bavEeProcPostSentenAutr = formulaire['bavEeProcPostSentenAutr'];

    return bavEeProcPostSentenTot !== null && bavEeProcPostSentenTot > bavEeProcPostSentenDec + bavEeProcPostSentenDef + bavEeProcPostSentenRecour
      + bavEeProcPostSentenAutr;
  }

  private alertF41Bav(formulaire: any): boolean {
    const bavEeProcPostSentenAutr = formulaire['bavEeProcPostSentenAutr'];
    const bavEeProcPostSentenAutrDet = formulaire['bavEeProcPostSentenAutrDet'];

    return bavEeProcPostSentenAutr !== null && bavEeProcPostSentenAutr > 0 && (bavEeProcPostSentenAutrDet === '' || bavEeProcPostSentenAutrDet === null);
  }

  private alertF16Bav(formulaire: any): boolean {
    const bavEeAuteurPreSentenTot = formulaire['bavEeAuteurPreSentenTot'];
    const bavEeAuteurPreSentenCj = formulaire['bavEeAuteurPreSentenCj'];
    const bavEeAuteurPreSentenDp = formulaire['bavEeAuteurPreSentenDp'];
    const bavEeAuteurPreSentenFinDeten = formulaire['bavEeAuteurPreSentenFinDeten'];
    const bavEeAuteurPreSentenFinMes = formulaire['bavEeAuteurPreSentenFinMes'];
    const bavEeAuteurPreSentenAutr = formulaire['bavEeAuteurPreSentenAutr'];

    return bavEeAuteurPreSentenTot !== null && bavEeAuteurPreSentenTot > bavEeAuteurPreSentenCj + bavEeAuteurPreSentenDp + bavEeAuteurPreSentenFinDeten
      + bavEeAuteurPreSentenFinMes + bavEeAuteurPreSentenAutr;
  }

  private alertF42Bav(formulaire: any): boolean {
    const bavEeAuteurPreSentenAutr = formulaire['bavEeAuteurPreSentenAutr'];
    const bavEeAuteurPreSentenAutrDet = formulaire['bavEeAuteurPreSentenAutrDet'];

    return bavEeAuteurPreSentenAutr !== null && bavEeAuteurPreSentenAutr > 0 && (bavEeAuteurPreSentenAutrDet === '' || bavEeAuteurPreSentenAutrDet === null);
  }

  private alertF17Bav(formulaire: any): boolean {
    const bavEeAuteurPostSentenTot = formulaire['bavEeAuteurPostSentenTot'];
    const bavEeAuteurPostSentenMilFer = formulaire['bavEeAuteurPostSentenMilFer'];
    const bavEeAuteurPostSentenMilOuv = formulaire['bavEeAuteurPostSentenMilOuv'];
    const bavEeAuteurPostSentenFinDeten = formulaire['bavEeAuteurPostSentenFinDeten'];
    const bavEeAuteurPostSentenFinMes = formulaire['bavEeAuteurPostSentenFinMes'];
    const bavEeAuteurPostSentenAutr = formulaire['bavEeAuteurPostSentenAutr'];

    return bavEeAuteurPostSentenTot !== null && bavEeAuteurPostSentenTot > bavEeAuteurPostSentenMilFer + bavEeAuteurPostSentenMilOuv + bavEeAuteurPostSentenFinDeten
      + bavEeAuteurPostSentenFinMes + bavEeAuteurPostSentenAutr;
  }

  private alertF43Bav(formulaire: any): boolean {
    const bavEeAuteurPostSentenAutr = formulaire['bavEeAuteurPostSentenAutr'];
    const bavEeAuteurPostSentenAutrDet = formulaire['bavEeAuteurPostSentenAutrDet'];

    return bavEeAuteurPostSentenAutr !== null && bavEeAuteurPostSentenAutr > 0 && (bavEeAuteurPostSentenAutrDet === '' || bavEeAuteurPostSentenAutrDet === null);
  }

  private alertF18Bav(formulaire: any): boolean {
    const bavEeDiPreSentenTot = formulaire['bavEeDiPreSentenTot'];
    const bavEeDiPreSentenCivi = formulaire['bavEeDiPreSentenCivi'];
    const bavEeDiPreSentenAutr = formulaire['bavEeDiPreSentenAutr'];

    return bavEeDiPreSentenTot !== null && bavEeDiPreSentenTot > bavEeDiPreSentenCivi + bavEeDiPreSentenAutr;
  }

  private alertF44Bav(formulaire: any): boolean {
    const bavEeDiPreSentenAutr = formulaire['bavEeDiPreSentenAutr'];
    const bavEeDiPreSentenAutrDet = formulaire['bavEeDiPreSentenAutrDet'];

    return bavEeDiPreSentenAutr !== null && bavEeDiPreSentenAutr > 0 && (bavEeDiPreSentenAutrDet === '' || bavEeDiPreSentenAutrDet === null);
  }

  private alertF19Bav(formulaire: any): boolean {
    const bavEeDiPostSentenTot = formulaire['bavEeDiPostSentenTot'];
    const bavEeDiPostSentenCivi = formulaire['bavEeDiPostSentenCivi'];
    const bavEeDiPostSentenSarvi = formulaire['bavEeDiPostSentenSarvi'];
    const bavEeDiPostSentenHuis = formulaire['bavEeDiPostSentenHuis'];
    const bavEeDiPostSentenJapSpip = formulaire['bavEeDiPostSentenJapSpip'];
    const bavEeDiPostSentenFondGar = formulaire['bavEeDiPostSentenFondGar'];
    const bavEeDiPostSentenAutr = formulaire['bavEeDiPostSentenAutr'];

    return bavEeDiPostSentenTot !== null && bavEeDiPostSentenTot > bavEeDiPostSentenCivi + bavEeDiPostSentenSarvi + bavEeDiPostSentenHuis
      + bavEeDiPostSentenJapSpip + bavEeDiPostSentenFondGar + bavEeDiPostSentenAutr;
  }

  private alertF45Bav(formulaire: any): boolean {
    const bavEeDiPostSentenAutr = formulaire['bavEeDiPostSentenAutr'];
    const bavEeDiPostSentenAutrDet = formulaire['bavEeDiPostSentenAutrDet'];

    return bavEeDiPostSentenAutr !== null && bavEeDiPostSentenAutr > 0 && (bavEeDiPostSentenAutrDet === '' || bavEeDiPostSentenAutrDet === null);
  }

  private alertF20Bav(formulaire: any): boolean {
    const bavEeDemarTot = formulaire['bavEeDemarTot'];
    const bavEeDemarPlainte = formulaire['bavEeDemarPlainte'];
    const bavEeDemarAj = formulaire['bavEeDemarAj'];
    const bavEeDemarAud = formulaire['bavEeDemarAud'];
    const bavEeDemarAccomp = formulaire['bavEeDemarAccomp'];
    const bavEeDemarDi = formulaire['bavEeDemarDi'];
    const bavEeDemarAutr = formulaire['bavEeDemarAutr'];

    return bavEeDemarTot !== null && bavEeDemarTot > bavEeDemarPlainte + bavEeDemarAj + bavEeDemarAud
      + bavEeDemarAccomp + bavEeDemarDi + bavEeDemarAutr;
  }

  private alertF48Bav(formulaire: any): boolean {
    const bavEeDemarAutr = formulaire['bavEeDemarAutr'];
    const bavEeDemarAutrDet = formulaire['bavEeDemarAutrDet'];

    return bavEeDemarAutr !== null && bavEeDemarAutr > 0 && (bavEeDemarAutrDet === '' || bavEeDemarAutrDet === null);
  }

}
